import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { doc, onSnapshot } from "firebase/firestore";
import AllInboxRoundedIcon from "@mui/icons-material/AllInboxRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AssignmentIndRoundedIcon from "@mui/icons-material/AssignmentIndRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import AudiotrackRoundedIcon from "@mui/icons-material/AudiotrackRounded";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import VideoCallOutlinedIcon from "@mui/icons-material/VideoCallOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import FormatBoldRoundedIcon from "@mui/icons-material/FormatBoldRounded";
import FormatItalicRoundedIcon from "@mui/icons-material/FormatItalicRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import FormatUnderlinedRoundedIcon from "@mui/icons-material/FormatUnderlinedRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import TextSnippetRoundedIcon from "@mui/icons-material/TextSnippetRounded";
import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { omnichannelInboxApi } from "../services/omnichannelInboxApi";
import { useAdaptivePolling } from "../hooks/useAdaptivePolling";
import { useComposeLock } from "../hooks/useComposeLock";
import { useDraftPersistence } from "../hooks/useDraftPersistence";
import "../styles/inbox.css";
import WhatsAppThreadView from "../components/omnichannel/WhatsAppThreadView";
import EmailThreadView from "../components/omnichannel/EmailThreadView";
import VoiceCallTimeline from "../components/omnichannel/VoiceCallTimeline";
import ConversationEmptyState from "../components/omnichannel/ConversationEmptyState";
import ConversationIntelligenceCard from "../components/omnichannel/ConversationIntelligenceCard";
import QuoteOpportunitiesDrawer from "../components/omnichannel/QuoteOpportunitiesDrawer";
import CustomerContextPanel from "../components/omnichannel/CustomerContextPanel";
import EmailProviderIcon, {
  emailProviderLabel,
  inferEmailProvider,
} from "../components/omnichannel/EmailProviderIcon";
import {
  AssignConversationDialog,
  FollowUpDialog,
  InitiateVoiceCallDialog,
  InternalNoteDialog,
  LinkInquiryDialog,
  VoiceCallDialog,
} from "../components/omnichannel/ConversationActionDialogs";
import { db as firestoreDb } from "../firebase";
import { listLines as listWhatsAppLines } from "../services/whatsappLinesApi";

const C = {
  bg: "linear-gradient(180deg, #FAFCFA 0%, #F5F8F5 100%)",
  shell: "#FFFFFF",
  border: "rgba(22, 49, 38, 0.08)",
  text: "#163126",
  subtext: "#708277",
  rail: "#FFFFFF",
  surface: "#FFFFFF",
  surfaceAlt: "#FAFBFA",
  surfaceStrong: "#FFFFFF",
  shellText: "#163126",
  shellSubtext: "#708277",
  shellMuted: "#7B8D84",
  whatsapp: "#2DB838",
  email: "#5F7468",
  voice: "#5F7468",
  amber: "#5F7468",
  sapphire: "#2DB838",
  mint: "#2DB838",
  unread: "#2DB838",
  shadow: "0 20px 44px -38px rgba(15, 23, 42, 0.1)",
};

const INBOX_TIME_ZONE = "Africa/Harare";

const CHANNEL_RAIL_ITEMS = [
  { value: "", label: "All", icon: <AllInboxRoundedIcon sx={{ fontSize: 21 }} />, color: C.amber },
  { value: "whatsapp", label: "WhatsApp", icon: <WhatsAppIcon sx={{ fontSize: 21 }} />, color: C.whatsapp },
  { value: "email", label: "Email", icon: <MailOutlineRoundedIcon sx={{ fontSize: 21 }} />, color: C.email },
  { value: "voice", label: "Voice", icon: <CallOutlinedIcon sx={{ fontSize: 21 }} />, color: C.voice },
];

const EMPTY_TODAY_OVERVIEW = {
  new_conversations: 0,
  active_conversations: 0,
  awaiting_conversations: 0,
  overdue_conversations: 0,
};

const makeFilters = (initialChannel) => ({
  q: "",
  assigned: "",
  group: "",
  created: "last_30_days",
  closedAt: "",
  status: "",
  priority: "",
  whatsappLineScope: "",
  whatsappLineId: "",
  channel: initialChannel || "",
});

const createNewConversationDraft = ({ channel = "whatsapp", whatsappLineId = "" } = {}) => ({
  channel,
  whatsappLineId,
  recipient: "",
  contactName: "",
  contactEmail: "",
  saveContact: channel === "whatsapp",
  subject: "",
  message: "",
});

const withChannelFilter = (filters, channel) => {
  const nextChannel = String(channel || "");

  if (nextChannel.toLowerCase() !== "whatsapp") {
    return {
      ...filters,
      channel: nextChannel,
      whatsappLineScope: "",
      whatsappLineId: "",
    };
  }

  return {
    ...filters,
    channel: nextChannel,
  };
};

const withWhatsAppLineScopeFilter = (filters, scope = "") => ({
  ...withChannelFilter(filters, "whatsapp"),
  whatsappLineScope: String(scope || ""),
  whatsappLineId: "",
});

const withWhatsAppLineIdFilter = (filters, lineId = "", fallbackScope = "") => ({
  ...withChannelFilter(filters, "whatsapp"),
  whatsappLineScope: lineId ? "" : String(fallbackScope || ""),
  whatsappLineId: lineId ? String(lineId) : "",
});

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "pending", label: "Pending" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
  { value: "waiting_customer", label: "Waiting on Customer" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const RELATIVE_WINDOW_OPTIONS = [
  { value: "", label: "Any time" },
  { value: "today", label: "Today" },
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
];

const initials = (name) =>
  String(name || "?")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const unwrapDetailPayload = (response) => {
  if (response?.data?.conversation) {
    return response.data;
  }

  if (response?.conversation) {
    return response;
  }

  return null;
};

// ── SLA Countdown helper ─────────────────────────────────────────────────────
const formatSlaCountdown = (dueAt, nowMs) => {
  if (!dueAt) return null;
  const dueMs = new Date(dueAt).getTime();
  if (Number.isNaN(dueMs)) return null;
  const diffMs = dueMs - nowMs;

  if (diffMs <= 0) {
    const over = Math.abs(diffMs);
    const h = Math.floor(over / 3_600_000);
    const m = Math.floor((over % 3_600_000) / 60_000);
    const label = h >= 1 ? `${h}h overdue` : `${m || 1}m overdue`;
    return { label, color: "#dc2626", bg: "#fef2f2", overdue: true };
  }

  const h = Math.floor(diffMs / 3_600_000);
  const m = Math.floor((diffMs % 3_600_000) / 60_000);

  if (diffMs >= 7_200_000) return { label: `${h}h ${m}m`, color: "#166534", bg: "#dcfce7", overdue: false };
  if (diffMs >= 1_800_000) return { label: h > 0 ? `${h}h ${m}m` : `${m}m`, color: "#92400e", bg: "#fef3c7", overdue: false };
  return { label: `${m || 1}m`, color: "#991b1b", bg: "#fee2e2", overdue: false };
};

const formatListTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const diffMs = now - date;

  if (diffMs < 60_000) return "just now";
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;

  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: INBOX_TIME_ZONE,
    }).format(date);
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

  if (diffMs < 7 * 86_400_000) {
    return date.toLocaleDateString([], { weekday: "short" });
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    timeZone: INBOX_TIME_ZONE,
  }).format(date);
};

const formatDetailTimestamp = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: INBOX_TIME_ZONE,
  }).format(date);
};

const sanitizePhoneForLink = (value) => String(value || "").replace(/[^\d]/g, "");

const journeyChipConfig = (label) => {
  switch (String(label || "").toLowerCase()) {
    case "first_time_inquiry":
      return { label: "First-time inquiry", tone: "neutral" };
    case "repeat_inquiry":
      return { label: "Repeat inquiry", tone: "success" };
    case "multi_channel_customer":
      return { label: "Multi-channel", tone: "info" };
    case "dormant_customer_reengaged":
      return { label: "Re-engaged", tone: "info" };
    case "escalation_prone_customer":
      return { label: "Escalation-prone", tone: "danger" };
    case "returning_customer":
      return { label: "Returning customer", tone: "success" };
    default:
      return { label: "Customer journey", tone: "neutral" };
  }
};

const quoteBehaviorChipConfig = (label) => {
  switch (String(label || "").toLowerCase()) {
    case "frequent_quotation_requester":
      return { label: "Frequent quotes", tone: "info" };
    case "repeat_flight_quote_requester":
      return { label: "Repeat flight quotes", tone: "neutral" };
    case "price_shopper":
      return { label: "Price shopper", tone: "warning" };
    case "negotiating_customer":
      return { label: "Negotiating", tone: "warning" };
    case "high_intent_returning_quote_lead":
      return { label: "High-intent lead", tone: "success" };
    default:
      return { label: "Quote behavior", tone: "neutral" };
  }
};

const nextBestActionChipConfig = (action) => {
  switch (String(action?.action_code || "").toLowerCase()) {
    case "manager_review_recommended":
      return { label: "Manager review", tone: "danger" };
    case "follow_up_sent_quote":
      return { label: "Follow up quote", tone: "warning" };
    case "collect_missing_quote_details":
      return { label: "Get quote details", tone: "warning" };
    case "assign_quote_owner":
      return { label: "Assign owner", tone: "warning" };
    case "revise_quote_offer":
      return { label: "Revise quote", tone: "info" };
    case "send_quote_to_customer":
      return { label: "Send quote", tone: "info" };
    case "confirm_quote_inquiry":
      return { label: "Confirm quote", tone: "info" };
    case "reply_to_unattended_inquiry":
      return { label: "Reply now", tone: "warning" };
    case "review_identity_profile":
      return { label: "Review profile", tone: "neutral" };
    default:
      return {
        label: action?.short_label || action?.label || "Next action",
        tone:
          action?.priority === "critical"
            ? "danger"
            : action?.priority === "high"
              ? "warning"
              : action?.priority === "medium"
                ? "info"
                : "neutral",
      };
  }
};

const ownershipSourceChipConfig = (ownership) => {
  const source = String(ownership?.source || "").toLowerCase();
  const label = String(ownership?.source_label || "").trim();

  if (!label) {
    return null;
  }

  if (source === "campaign_launcher") {
    return { key: "ownership_source", label: "Campaign launcher", tone: "success" };
  }

  if (source === "rotation_owner") {
    return { key: "ownership_source", label: "Rotation owner", tone: "info" };
  }

  if (source === "line_owner" || source === "line_owner_fallback") {
    return { key: "ownership_source", label: "Line owner", tone: "neutral" };
  }

  return { key: "ownership_source", label, tone: "neutral" };
};

// eslint-disable-next-line no-unused-vars
const truncateSummaryText = (value, maxLength = 160) => {
  const text = String(value || "").trim();

  if (!text) {
    return "";
  }

  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;
};

const toDateMs = (value) => {
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const toConversationActivityMs = (conversation) =>
  toDateMs(
    conversation?.last_activity_at ||
      conversation?.last_message_at ||
      conversation?.updated_at ||
      conversation?.created_at
  );

// SLA urgency rank: breached > at_risk > everything else
const slaUrgencyRank = (conv) => {
  const status = String(conv?.sla_status || "").toLowerCase();
  if (status === "breached") return 3;
  // Near-breach: first_response_due_at within next 30 minutes
  const due = conv?.first_response_due_at ? new Date(conv.first_response_due_at).getTime() : 0;
  if (due && due - Date.now() < 1_800_000 && due > Date.now()) return 2; // < 30 min remaining
  if (status === "at_risk") return 1;
  return 0;
};

const sortConversationsByActivity = (conversations = []) =>
  [...conversations].sort((left, right) => {
    // SLA-urgent conversations always float to top
    const slaDelta = slaUrgencyRank(right) - slaUrgencyRank(left);
    if (slaDelta !== 0) return slaDelta;

    // 🔥 Hot clients float above regular conversations (but below SLA breached)
    const leftHot  = left?.contact?.is_hot_client ? 1 : 0;
    const rightHot = right?.contact?.is_hot_client ? 1 : 0;
    if (rightHot !== leftHot) return rightHot - leftHot;

    const timeDelta = toConversationActivityMs(right) - toConversationActivityMs(left);
    if (timeDelta !== 0) return timeDelta;

    return (right?.id || 0) - (left?.id || 0);
  });

const toMessageOccurredMs = (message) => toDateMs(message?.occurred_at || message?.created_at);

const sortMessagesChronologically = (messages = []) =>
  [...messages].sort((left, right) => {
    const timeDelta = toMessageOccurredMs(left) - toMessageOccurredMs(right);
    if (timeDelta !== 0) {
      return timeDelta;
    }

    return (left?.id || 0) - (right?.id || 0);
  });

const conversationListSnapshot = (conversations = []) =>
  JSON.stringify(
    conversations.map((conversation) => ({
      id: conversation?.id || null,
      channel: conversation?.channel || null,
      status: conversation?.status || null,
      priority: conversation?.priority || null,
      unread_count: conversation?.unread_count || 0,
      customer_name: conversation?.customer?.name || conversation?.contact?.name || null,
      subject: conversation?.subject || null,
      last_message_preview: conversation?.last_message_preview || null,
      last_message_at: conversation?.last_message_at || null,
      last_activity_at: conversation?.last_activity_at || null,
      updated_at: conversation?.updated_at || null,
      whatsapp_line_id: conversation?.whatsapp_line?.id || null,
      whatsapp_line_name: conversation?.whatsapp_line?.display_name || null,
      whatsapp_line_type: conversation?.whatsapp_line?.line_type || null,
      whatsapp_line_provider: conversation?.whatsapp_line?.provider || null,
      active_quote_id: conversation?.active_quote?.id || null,
      active_quote_status: conversation?.active_quote?.status || null,
      is_hot_client: conversation?.contact?.is_hot_client || false,
      assigned_to_id:
        conversation?.assignment?.assigned_to?.id
        || conversation?.assigned_to?.id
        || null,
      lock_locked: Boolean(conversation?.lock?.locked),
      lock_locked_by_id: conversation?.lock?.locked_by?.id || null,
      reply_configured:
        typeof conversation?.reply?.configured === "boolean"
          ? conversation.reply.configured
          : null,
      permissions_can_reply:
        typeof conversation?.permissions?.can_reply === "boolean"
          ? conversation.permissions.can_reply
          : null,
      permissions_can_override_reply:
        typeof conversation?.permissions?.can_override_reply === "boolean"
          ? conversation.permissions.can_override_reply
          : null,
      permissions_requires_claim:
        typeof conversation?.permissions?.requires_claim === "boolean"
          ? conversation.permissions.requires_claim
          : null,
      quote_badges: Array.isArray(conversation?.quote_badges)
        ? conversation.quote_badges.map((badge) => `${badge.kind}:${badge.label}:${badge.tone}`)
        : [],
      email_provider: conversation?.metadata?.provider || null,
      mailbox_id: conversation?.mailbox?.id || null,
    }))
  );

const conversationDetailSnapshot = (conversation, messages = [], voiceCalls = []) =>
  JSON.stringify({
    conversation: conversation
      ? {
          id: conversation?.id || null,
          channel: conversation?.channel || null,
          status: conversation?.status || null,
          priority: conversation?.priority || null,
          subject: conversation?.subject || null,
          last_message_preview: conversation?.last_message_preview || null,
          last_message_at: conversation?.last_message_at || null,
          last_activity_at: conversation?.last_activity_at || null,
          updated_at: conversation?.updated_at || null,
          whatsapp_line_id: conversation?.whatsapp_line?.id || null,
          whatsapp_line_name: conversation?.whatsapp_line?.display_name || null,
          whatsapp_line_type: conversation?.whatsapp_line?.line_type || null,
          whatsapp_line_provider: conversation?.whatsapp_line?.provider || null,
          customer_name: conversation?.customer?.name || conversation?.contact?.name || null,
          active_quote_id: conversation?.active_quote?.id || null,
          active_quote_status: conversation?.active_quote?.status || null,
          assigned_to_id:
            conversation?.assignment?.assigned_to?.id
            || conversation?.assigned_to?.id
            || null,
          lock_locked: Boolean(conversation?.lock?.locked),
          lock_locked_by_id: conversation?.lock?.locked_by?.id || null,
          reply_configured:
            typeof conversation?.reply?.configured === "boolean"
              ? conversation.reply.configured
              : null,
          permissions_can_reply:
            typeof conversation?.permissions?.can_reply === "boolean"
              ? conversation.permissions.can_reply
              : null,
          permissions_can_override_reply:
            typeof conversation?.permissions?.can_override_reply === "boolean"
              ? conversation.permissions.can_override_reply
              : null,
          permissions_requires_claim:
            typeof conversation?.permissions?.requires_claim === "boolean"
              ? conversation.permissions.requires_claim
              : null,
          quote_follow_up_due: conversation?.quote_follow_up_due || null,
          quote_badges: Array.isArray(conversation?.quote_badges)
            ? conversation.quote_badges.map((badge) => `${badge.kind}:${badge.label}:${badge.tone}`)
            : [],
          next_best_action_code: conversation?.next_best_action?.action_code || null,
          next_best_action_priority: conversation?.next_best_action?.priority || null,
          quote_missing_fields: Array.isArray(conversation?.quote_missing_fields)
            ? conversation.quote_missing_fields
            : [],
        }
      : null,
    messages: messages.map((message) => ({
      id: message?.id || null,
      direction: message?.direction || null,
      message_type: message?.message_type || null,
      status: message?.status || null,
      occurred_at: message?.occurred_at || null,
      body_text: message?.body?.text || null,
    })),
    voice_calls: voiceCalls.map((voiceCall) => ({
      id: voiceCall?.id || null,
      status: voiceCall?.status || null,
      occurred_at: voiceCall?.occurred_at || null,
    })),
  });

const stripConversationPermissionState = (conversation) =>
  conversation
    ? {
        ...conversation,
        permissions: null,
      }
    : null;

const formatRelativeActivity = (value, nowMs = Date.now()) => {
  const timestampMs = value instanceof Date ? value.getTime() : toDateMs(value);
  if (!timestampMs) return "";

  const diffSeconds = Math.max(0, Math.floor((nowMs - timestampMs) / 1000));

  if (diffSeconds < 45) {
    return "just now";
  }

  if (diffSeconds < 3600) {
    const minutes = Math.max(1, Math.floor(diffSeconds / 60));
    return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
  }

  if (diffSeconds < 86400) {
    const hours = Math.max(1, Math.floor(diffSeconds / 3600));
    return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  }

  const days = Math.max(1, Math.floor(diffSeconds / 86400));
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const deriveLivePresenceStatus = ({ presenceSnapshot, inquiryWorkspaceId, nowMs = Date.now() }) => {
  if (!presenceSnapshot || !inquiryWorkspaceId) {
    return null;
  }

  const { isOnline, lastSeen, currentInquiryId } = presenceSnapshot;

  if (!currentInquiryId || String(currentInquiryId) !== String(inquiryWorkspaceId)) {
    return null;
  }

  const lastSeenValue = lastSeen?.toDate ? lastSeen.toDate() : lastSeen;
  const lastSeenMs = toDateMs(lastSeenValue);

  if (Boolean(isOnline) && lastSeenMs && nowMs - lastSeenMs <= 20000) {
    return {
      label: "Online",
      helper: "Live chat customer is active now",
      tone: "success",
      source: "live_presence",
    };
  }

  if (!lastSeenMs) {
    return {
      label: "Offline",
      helper: "Live chat customer is currently away",
      tone: "muted",
      source: "live_presence",
    };
  }

  return {
    label: "Last seen",
    helper: formatRelativeActivity(lastSeenValue, nowMs),
    tone: "muted",
    source: "live_presence",
  };
};

const deriveInferredActivityStatus = ({ conversation, messages = [], nowMs = Date.now() }) => {
  const channel = String(conversation?.channel || "").toLowerCase();
  const activityNoun = channel === "email" ? "email" : channel === "voice" ? "call" : "message";
  const inboundMessages = Array.isArray(messages)
    ? messages.filter(
        (message) =>
          message?.direction === "inbound"
          && !["system", "internal_note"].includes(String(message?.message_type || "").toLowerCase())
      )
    : [];
  const latestInboundMessage = inboundMessages.length ? inboundMessages[inboundMessages.length - 1] : null;
  const latestInboundAt = latestInboundMessage?.occurred_at || null;
  const latestInboundMs = toDateMs(latestInboundAt);

  if (latestInboundMs) {
    const ageMs = Math.max(0, nowMs - latestInboundMs);

    if (ageMs <= 2 * 60 * 1000) {
      return {
        label: "Active now",
        helper: `Recent ${activityNoun} activity`,
        tone: "success",
        source: "inferred",
      };
    }

    if (ageMs <= 10 * 60 * 1000) {
      return {
        label: "Recently active",
        helper: `Last ${activityNoun} ${formatRelativeActivity(latestInboundAt, nowMs)}`,
        tone: "success",
        source: "inferred",
      };
    }

    if (ageMs <= 60 * 60 * 1000) {
      return {
        label: "Seen this hour",
        helper: `Last ${activityNoun} ${formatRelativeActivity(latestInboundAt, nowMs)}`,
        tone: "neutral",
        source: "inferred",
      };
    }

    return {
      label: "Last customer activity",
      helper: formatRelativeActivity(latestInboundAt, nowMs),
      tone: "muted",
      source: "inferred",
    };
  }

  const fallbackAt = conversation?.last_activity_at || conversation?.last_message_at || conversation?.created_at;
  if (fallbackAt) {
    return {
      label: "No recent customer activity",
      helper: `Thread updated ${formatRelativeActivity(fallbackAt, nowMs)}`,
      tone: "muted",
      source: "inferred",
    };
  }

  return {
    label: "Awaiting activity",
    helper: "No customer activity yet",
    tone: "muted",
    source: "inferred",
  };
};

const deriveCustomerActivityStatus = ({
  conversation,
  messages = [],
  presenceSnapshot,
  inquiryWorkspaceId,
  nowMs = Date.now(),
}) =>
  deriveLivePresenceStatus({
    presenceSnapshot,
    inquiryWorkspaceId,
    nowMs,
  }) ||
  deriveInferredActivityStatus({
    conversation,
    messages,
    nowMs,
  });

const getEmailProviderAccent = (provider) => {
  switch (inferEmailProvider(provider)) {
    case "gmail":
      return "#D93025";
    case "webmail":
      return "#1E874E";
    default:
      return C.email;
  }
};

const resolveConversationEmailProvider = (conversation) =>
  inferEmailProvider({
    provider: conversation?.metadata?.provider,
    metadata: conversation?.metadata,
    mailbox: conversation?.mailbox,
    // Fall back to the raw DB field so list-view items (which carry
    // user_mailbox_id but not the full mailbox object) still resolve
    // to "webmail" and render the branded coloured icon.
    user_mailbox_id: conversation?.mailbox?.id || conversation?.user_mailbox_id,
  });

const emailChannelMeta = (conversation = null) => {
  const provider = resolveConversationEmailProvider(conversation);

  return {
    label: emailProviderLabel(provider),
    icon: <EmailProviderIcon provider={provider} size={15} />,
    color: getEmailProviderAccent(provider),
  };
};

const channelMeta = (channel, conversation = null) => {
  switch (String(channel || "").toLowerCase()) {
    case "whatsapp":
      return {
        label: "WhatsApp",
        icon: <WhatsAppIcon sx={{ fontSize: 15 }} />,
        color: C.whatsapp,
      };
    case "voice":
      return {
        label: "Voice",
        icon: <CallOutlinedIcon sx={{ fontSize: 15 }} />,
        color: C.voice,
      };
    default:
      return emailChannelMeta(conversation);
  }
};

const normalizeWhatsAppLineType = (source) => {
  const rawType = String(
    source?.whatsapp_line?.line_type
    || source?.line_type
    || ""
  ).toLowerCase();

  if (rawType === "broadcast" || rawType === "main") {
    return "broadcast";
  }

  if (rawType === "rotated_work") {
    return "rotated_work";
  }

  if (rawType === "consultant") {
    return "consultant";
  }

  return String(source?.channel || "").toLowerCase() === "whatsapp" ? "broadcast" : "";
};

const whatsappLineTypeLabel = (lineType) => {
  switch (normalizeWhatsAppLineType({ line_type: lineType })) {
    case "broadcast":
      return "Broadcast";
    case "rotated_work":
      return "Rotated Work";
    case "consultant":
      return "Direct";
    default:
      return "WhatsApp";
  }
};

const getWhatsAppLineScope = (conversation) => {
  if (String(conversation?.channel || "").toLowerCase() !== "whatsapp") {
    return null;
  }

  return normalizeWhatsAppLineType(conversation);
};

const getWhatsAppProviderLabel = (conversation) => {
  const provider = String(conversation?.whatsapp_line?.provider || "").toLowerCase();

  if (provider === "web_bridge") {
    return "Legacy bridge";
  }

  if (provider === "twilio") {
    return "Twilio";
  }

  return "Cloud API";
};

const getCompactOwnerName = (name) => {
  const trimmed = String(name || "").trim();
  if (!trimmed) {
    return "";
  }

  return trimmed.split(/\s+/)[0];
};

const getWhatsAppLinePresentation = (conversation) => {
  const scope = getWhatsAppLineScope(conversation);

  if (scope === "broadcast") {
    return {
      scope: "broadcast",
      label: "Broadcast",
      detailLabel: conversation?.whatsapp_line?.display_name || "Broadcast Number",
      descriptor: "Campaign replies and outreach responses",
      providerLabel: getWhatsAppProviderLabel(conversation),
      phoneNumber: conversation?.whatsapp_line?.phone_number || "",
      ownerName: "",
      accent: "#C17A08",
      background: "#FFF4D8",
      color: "#A76400",
      border: "#F5D18A",
      surface: "linear-gradient(135deg, rgba(255,244,216,0.92) 0%, rgba(255,250,240,0.98) 100%)",
      mutedSurface: "#FFF9ED",
    };
  }

  if (scope === "rotated_work") {
    const line = conversation?.whatsapp_line || {};

    return {
      scope: "rotated_work",
      label: "Rotated Work Line",
      detailLabel: line?.display_name || "Rotated Work Line",
      descriptor: "Shared weekly consultant line",
      providerLabel: getWhatsAppProviderLabel(conversation),
      phoneNumber: line?.phone_number || "",
      ownerName: line?.owner?.name || "",
      accent: "#0F766E",
      background: "#EAFBF7",
      color: "#0F766E",
      border: "#BDEBE0",
      surface: "linear-gradient(135deg, rgba(234,251,247,0.96) 0%, rgba(247,255,252,0.98) 100%)",
      mutedSurface: "#F4FFFB",
    };
  }

  const line = conversation?.whatsapp_line || {};
  const ownerName = line?.owner?.name || "";
  const shortOwner = getCompactOwnerName(ownerName);
  const shortLabel = shortOwner ? `${shortOwner} Line` : (line?.display_name || "Consultant Line");

  return {
    scope: "consultant",
    label: shortLabel,
    detailLabel: line?.display_name || shortLabel,
    descriptor: shortOwner ? `${shortOwner}'s work line` : "Consultant direct line",
    providerLabel: getWhatsAppProviderLabel(conversation),
    phoneNumber: line?.phone_number || "",
    ownerName,
    accent: "#0F766E",
    background: "#E6F8F3",
    color: "#0F766E",
    border: "#B9E8D1",
    surface: "linear-gradient(135deg, rgba(230,248,243,0.95) 0%, rgba(245,253,250,0.98) 100%)",
    mutedSurface: "#F2FCF8",
  };
};

const conversationHasHotSignal = (conversation) => {
  if (!conversation) {
    return false;
  }

  const priority = String(conversation?.priority || conversation?.inquiry?.priority || "").toLowerCase();
  const temperature = String(
    conversation?.temperature
    || conversation?.lead_status
    || conversation?.inquiry?.temperature
    || conversation?.inquiry?.lead_status
    || ""
  ).toLowerCase();

  return Boolean(
    conversation?.contact?.is_hot_client
    || ["high", "urgent"].includes(priority)
    || temperature === "hot"
    || (Array.isArray(conversation?.quote_badges)
      && conversation.quote_badges.some((badge) =>
        String(badge?.label || "").toLowerCase().includes("hot")
      ))
  );
};

const ROW_BADGE_LABEL_OVERRIDES = {
  "potential quote": "Potential",
  "qualified quote": "Qualified",
  "draft quote": "Draft",
  "quote sent": "Sent",
  "awaiting customer": "Awaiting",
  "follow-up due": "Follow-up",
  "expiry risk": "Expiring",
};

const normalizeConversationRowBadgeLabel = (label) => {
  const raw = String(label || "").trim();
  if (!raw) {
    return "";
  }

  return ROW_BADGE_LABEL_OVERRIDES[raw.toLowerCase()] || raw;
};

const isHotBadge = (badge) => String(badge?.label || "").trim().toLowerCase() === "hot";

const getPrimaryConversationRowBadge = (badges = []) => {
  const seen = new Set();

  return badges
    .filter((badge) => badge && badge.label)
    .map((badge) => ({
      ...badge,
      label: normalizeConversationRowBadgeLabel(badge.label),
    }))
    .filter((badge) => {
      const key = String(badge?.label || "").trim().toLowerCase();
      if (!key || seen.has(key) || isHotBadge(badge)) {
        return false;
      }
      seen.add(key);
      return true;
    })[0] || null;
};

const composerAttachmentKind = (file) => {
  const type = String(file?.type || "").toLowerCase();

  if (type.startsWith("image/")) {
    return "image";
  }

  if (type.startsWith("audio/")) {
    return "audio";
  }

  if (type.startsWith("video/")) {
    return "video";
  }

  return "document";
};

const composerAttachmentSize = (value) => {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) {
    return "";
  }

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const makeComposerAttachment = (file) => {
  const kind = composerAttachmentKind(file);
  const canPreview = ["image", "audio", "video"].includes(kind);
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    file,
    kind,
    name: file?.name || "attachment",
    size: Number(file?.size || 0),
    previewUrl: canPreview ? URL.createObjectURL(file) : "",
  };
};

const revokeComposerAttachmentPreviews = (attachments = []) => {
  attachments.forEach((attachment) => {
    if (attachment?.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl);
    }
  });
};

const recordingMimeType = () => {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return "";
  }

  const candidates = [
    "audio/ogg;codecs=opus",
    "audio/ogg",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/webm;codecs=opus",
    "audio/webm",
  ];

  return candidates.find((candidate) => window.MediaRecorder.isTypeSupported?.(candidate)) || "";
};

const formatComposerAudioTime = (value) => {
  if (!Number.isFinite(value) || value < 0) {
    return "0:00";
  }

  const wholeSeconds = Math.floor(value);
  const minutes = Math.floor(wholeSeconds / 60);
  const seconds = wholeSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const COMPOSER_WAVE_BARS = [10, 15, 12, 18, 14, 11, 19, 13, 17, 12, 9, 16, 11, 15, 10, 18, 12, 14];

const attachmentIcon = (kind) => {
  switch (kind) {
    case "image":
      return <ImageRoundedIcon sx={{ fontSize: 18 }} />;
    case "audio":
      return <AudiotrackRoundedIcon sx={{ fontSize: 18 }} />;
    case "video":
      return <VideoLibraryRoundedIcon sx={{ fontSize: 18 }} />;
    default:
      return <InsertDriveFileRoundedIcon sx={{ fontSize: 18 }} />;
  }
};

function ComposerAttachmentStrip({ attachments = [], onRemove }) {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuAttachmentId, setMenuAttachmentId] = useState("");
  const [playingAttachmentId, setPlayingAttachmentId] = useState("");
  const [audioDurations, setAudioDurations] = useState({});
  const [audioPositions, setAudioPositions] = useState({});
  const audioRefs = useRef({});

  if (!attachments.length) {
    return null;
  }

  const handleOpenMenu = (event, attachmentId) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuAttachmentId(attachmentId);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuAttachmentId("");
  };

  const handleDeleteFromMenu = () => {
    if (menuAttachmentId) {
      onRemove(menuAttachmentId);
    }

    handleCloseMenu();
  };

  const handleToggleAudioPlayback = async (attachment) => {
    if (!attachment?.id || !attachment?.previewUrl) {
      return;
    }

    const currentAudio = audioRefs.current[attachment.id];
    if (!currentAudio) {
      return;
    }

    if (playingAttachmentId && playingAttachmentId !== attachment.id) {
      const previousAudio = audioRefs.current[playingAttachmentId];
      previousAudio?.pause?.();
    }

    if (currentAudio.paused) {
      try {
        await currentAudio.play();
        setPlayingAttachmentId(attachment.id);
      } catch (_error) {
        setPlayingAttachmentId("");
      }
      return;
    }

    currentAudio.pause();
    setPlayingAttachmentId("");
  };

  return (
    <Box sx={{ px: 0.15, pb: 0.9 }}>
      <Stack direction="row" spacing={0.8} sx={{ overflowX: "auto", pb: 0.2 }}>
        {attachments.map((attachment) => (
          <Paper
            key={attachment.id}
            elevation={0}
            sx={{
              minWidth: 164,
              maxWidth: 220,
              borderRadius: 2.5,
              border: "1px solid #DCE5ED",
              bgcolor: "#FFFFFF",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {attachment.kind === "audio" ? (
              <Box sx={{ px: 1.05, py: 0.95 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton
                    onClick={() => handleToggleAudioPlayback(attachment)}
                    sx={{
                      width: 38,
                      height: 38,
                      flexShrink: 0,
                      bgcolor: "#25D366",
                      color: "#FFFFFF",
                      "&:hover": { bgcolor: "#1fb55a" },
                    }}
                  >
                    {playingAttachmentId === attachment.id ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
                  </IconButton>

                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Stack direction="row" spacing={0.35} alignItems="center" sx={{ height: 24 }}>
                      {COMPOSER_WAVE_BARS.map((height, index) => {
                        const duration = audioDurations[attachment.id] || 0;
                        const position = audioPositions[attachment.id] || 0;
                        const progressRatio = duration > 0 ? Math.min(position / duration, 1) : 0;
                        const activeBars = Math.max(1, Math.round(progressRatio * COMPOSER_WAVE_BARS.length));

                        return (
                          <Box
                            key={`${attachment.id}-${index}`}
                            sx={{
                              width: 4,
                              height,
                              borderRadius: 999,
                              bgcolor: index < activeBars ? "#25D366" : alpha("#6C7A89", 0.35),
                              transition: "background-color 120ms ease",
                            }}
                          />
                        );
                      })}
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.4 }}>
                      <Typography variant="caption" sx={{ color: "#728395", fontWeight: 600 }}>
                        Voice note
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#728395", fontWeight: 700 }}>
                        {formatComposerAudioTime(
                          playingAttachmentId === attachment.id
                            ? (audioPositions[attachment.id] || 0)
                            : (audioDurations[attachment.id] || 0)
                        )}
                      </Typography>
                    </Stack>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={(event) => handleOpenMenu(event, attachment.id)}
                    sx={{ width: 28, height: 28, flexShrink: 0 }}
                  >
                    <MoreVertRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Stack>

                <audio
                  ref={(node) => {
                    if (node) {
                      audioRefs.current[attachment.id] = node;
                    } else {
                      delete audioRefs.current[attachment.id];
                    }
                  }}
                  preload="metadata"
                  src={attachment.previewUrl || undefined}
                  style={{ display: "none" }}
                  onLoadedMetadata={(event) => {
                    const nextDuration = event?.currentTarget?.duration || 0;
                    setAudioDurations((prev) => ({
                      ...prev,
                      [attachment.id]: nextDuration,
                    }));
                  }}
                  onTimeUpdate={(event) => {
                    const nextPosition = event?.currentTarget?.currentTime || 0;
                    setAudioPositions((prev) => ({
                      ...prev,
                      [attachment.id]: nextPosition,
                    }));
                  }}
                  onPause={() => {
                    setPlayingAttachmentId((prev) => (prev === attachment.id ? "" : prev));
                  }}
                  onEnded={(event) => {
                    const audioElement = event?.currentTarget || null;
                    setPlayingAttachmentId("");
                    setAudioPositions((prev) => ({
                      ...prev,
                      [attachment.id]: 0,
                    }));
                    if (audioElement) {
                      audioElement.currentTime = 0;
                    }
                  }}
                >
                  Your browser does not support audio playback.
                </audio>
              </Box>
            ) : null}

            {attachment.kind === "image" && attachment.previewUrl ? (
              <Box
                component="img"
                src={attachment.previewUrl}
                alt={attachment.name}
                sx={{ display: "block", width: "100%", height: 90, objectFit: "cover", bgcolor: "#EFF4F8" }}
              />
            ) : null}

            {attachment.kind === "video" && attachment.previewUrl ? (
              <Box sx={{ px: 1, pt: 1, pb: 0.2 }}>
                <video controls preload="metadata" src={attachment.previewUrl} style={{ width: "100%", borderRadius: 12, background: "#000" }}>
                  Your browser does not support video playback.
                </video>
              </Box>
            ) : null}

            {attachment.kind !== "audio" ? (
              <Box sx={{ px: 1, py: 0.9 }}>
              <Stack direction="row" spacing={0.9} alignItems="flex-start">
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "10px",
                    bgcolor: "#F3F7FB",
                    color: "#5F7387",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {attachmentIcon(attachment.kind)}
                </Box>

                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#183149", wordBreak: "break-word" }}>
                    {attachment.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#728395", display: "block", mt: 0.2 }}>
                    {composerAttachmentSize(attachment.size) || "Attachment"}
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  onClick={() => onRemove(attachment.id)}
                  sx={{ width: 28, height: 28, flexShrink: 0, mt: -0.2 }}
                >
                  <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
              </Box>
            ) : null}
          </Paper>
        ))}
      </Stack>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleDeleteFromMenu}>Delete</MenuItem>
      </Menu>
    </Box>
  );
}

const groupByContact = (rows) => {
  const groups = new Map();
  for (const item of rows) {
    const email = String(item?.customer?.email || "").trim().toLowerCase();
    const phone = String(item?.customer?.phone || "").trim().replace(/\s+/g, "");
    const name = String(item?.customer?.name || "unknown").trim().toLowerCase();
    const key = email ? `e:${email}` : phone ? `p:${phone}` : `n:${name}`;
    if (!groups.has(key)) groups.set(key, { key, items: [] });
    groups.get(key).items.push(item);
  }
  return Array.from(groups.values());
};

function ConversationListRow({ item, active, onClick, showLineBadge = true, nowMs }) {
  const customerName = item?.customer?.name || "Unknown";
  const preview = item?.last_message_preview || item?.subject || "No recent message";
  const activityAt = item?.last_activity_at || item?.last_message_at || item?.created_at;
  const quoteBadges = Array.isArray(item?.quote_badges) ? item.quote_badges : [];
  const rowChannel = String(item?.channel || "").toLowerCase();
  const emailProvider = rowChannel === "email" ? resolveConversationEmailProvider(item) : null;
  const channelIcon = rowChannel === "whatsapp"
    ? <WhatsAppIcon sx={{ fontSize: 11, color: C.whatsapp, flexShrink: 0 }} />
    : rowChannel === "voice"
    ? <CallOutlinedIcon sx={{ fontSize: 11, color: C.voice, flexShrink: 0 }} />
    : <EmailProviderIcon provider={emailProvider} size={11} sx={{ flexShrink: 0 }} />;
  const unreadCount = Number(item?.unread_count || 0);
  const isHotClient = conversationHasHotSignal(item);
  const whatsAppLine = rowChannel === "whatsapp" ? getWhatsAppLinePresentation(item) : null;
  const primaryQuoteBadge = getPrimaryConversationRowBadge(quoteBadges);
  const baseRowSurface = active ? "#F1FBF4" : "#FFFFFF";
  const hoverRowSurface = active ? "#ECF8F0" : "#F8FBF8";

  // SLA live countdown — nowMs is provided by the parent (activityNowMs) which
  // already ticks every 30 s, eliminating one interval per row.
  const resolvedNowMs = nowMs ?? Date.now();
  const sla = formatSlaCountdown(item?.first_response_due_at, resolvedNowMs);

  // Avatar color — stable per first letter
  const AV_PALETTES = [
    { bg: "#dcfce7", color: "#166534" },
    { bg: "#e0f2fe", color: "#0369a1" },
    { bg: "#fef3c7", color: "#92400e" },
    { bg: "#f3f4f6", color: "#6b7280" },
    { bg: "#ede9fe", color: "#5b21b6" },
  ];
  const pal = AV_PALETTES[(customerName.charCodeAt(0) || 0) % AV_PALETTES.length];
  const avBg = active ? "#dcfce7" : pal.bg;
  const avColor = active ? "#166534" : pal.color;

  const badgeStyle = (badge) => {
    if (badge.tone === "success") return { background: "#dcfce7", color: "#166534" };
    if (badge.tone === "warning") return { background: "#fef9c3", color: "#854d0e" };
    if (badge.tone === "info")    return { background: "#e0f2fe", color: "#0369a1" };
    if (badge.tone === "danger")  return { background: "#FEF2F2", color: "#DC2626" };
    return { background: "#f3f4f6", color: "#6b7280" };
  };
  const primaryQuoteBadgeStyle = primaryQuoteBadge ? badgeStyle(primaryQuoteBadge) : null;
  const showHotBadge = isHotClient && (!primaryQuoteBadge || !["danger", "warning"].includes(primaryQuoteBadge.tone));
  const showSlaBadge = Boolean(sla?.overdue) && !primaryQuoteBadge;

  return (
    <Box
      onClick={() => onClick?.(item)}
      sx={{
        px: "12px",
        py: "9px",
        borderBottom: "1px solid #F1F4F2",
        cursor: "pointer",
        display: "flex",
        gap: "9px",
        position: "relative",
        bgcolor: baseRowSurface,
        borderLeft: active
          ? "3px solid #16a34a"
            : whatsAppLine
              ? `3px solid ${alpha(whatsAppLine.accent, unreadCount ? 0.8 : 0.42)}`
            : isHotClient
              ? "3px solid #f97316"
              : unreadCount
                ? `3px solid ${C.whatsapp}`
                : "3px solid transparent",
        "&:hover": { bgcolor: hoverRowSurface },
        transition: "background-color 120ms ease, border-color 120ms ease",
        touchAction: "manipulation",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {/* Avatar */}
      <Box sx={{
        width: 34, height: 34, borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 700, flexShrink: 0, mt: "1px",
        bgcolor: avBg, color: avColor,
        fontFamily: "inherit",
      }}>
        {initials(customerName)}
      </Box>

      {/* Content */}
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        {/* Row 1: name + timestamp */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "4px" }}>
          <Box sx={{
            fontSize: 13, fontWeight: 700, color: "#111111",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
            display: "flex", alignItems: "center", gap: "3px",
            fontFamily: "inherit",
          }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{customerName}</span>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "3px", flexShrink: 0 }}>
            <Box sx={{ color: "#BAC6BF", display: "inline-flex", alignItems: "center" }}>
              {channelIcon}
            </Box>
            <Box sx={{ fontSize: 10, color: "#9AA9A0", fontFamily: "inherit", fontWeight: 500 }}>
              {formatListTimestamp(activityAt)}
            </Box>
          </Box>
        </Box>

        {/* Row 2: preview */}
        <Box sx={{
          fontSize: 11, color: "#7F8E86",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          maxWidth: "100%", mt: "3px", fontFamily: "inherit",
        }}>
          {preview}
        </Box>

        {/* Badges + SLA */}
        {((showLineBadge && whatsAppLine) || showHotBadge || primaryQuoteBadge || showSlaBadge) && (
          <Box sx={{ display: "flex", gap: "3px", mt: "4px", flexWrap: "wrap", alignItems: "center" }}>
            {showLineBadge && whatsAppLine ? (
              <Box
                sx={{
                  fontSize: 8.5,
                  fontWeight: 600,
                  px: "6px",
                  py: "1.5px",
                  borderRadius: "20px",
                  background: alpha(whatsAppLine.accent, 0.06),
                  color: alpha(whatsAppLine.color, 0.75),
                  fontFamily: "inherit",
                  maxWidth: 120,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {whatsAppLine.label}
              </Box>
            ) : null}
            {showHotBadge && (
              <Box sx={{
                fontSize: 8.5, fontWeight: 700,
                px: "6px", py: "1.5px", borderRadius: "20px",
                background: "#FEF2F2", color: "#DC2626",
                fontFamily: "inherit",
              }}>
                HOT
              </Box>
            )}
            {primaryQuoteBadge && primaryQuoteBadgeStyle ? (
              <Box sx={{
                fontSize: 8.5, fontWeight: 600,
                px: "6px", py: "1.5px", borderRadius: "20px",
                background: primaryQuoteBadgeStyle.background,
                color: primaryQuoteBadgeStyle.color,
                fontFamily: "inherit",
              }}>
                {primaryQuoteBadge.label}
              </Box>
            ) : null}
            {showSlaBadge && sla ? (
              <Box sx={{
                fontSize: 8.5, fontWeight: 700,
                px: "6px", py: "1.5px", borderRadius: "20px",
                background: sla.bg, color: sla.color,
                fontFamily: "inherit",
              }}>
                {sla.label}
              </Box>
            ) : null}
          </Box>
        )}
      </Box>

      {/* Unread count badge */}
      {unreadCount > 0 && !active && (
        <Box sx={{
          minWidth: 18, height: 18, px: "3px",
          borderRadius: "999px", bgcolor: C.whatsapp, color: "#FFFFFF",
          fontSize: 9, fontWeight: 800, lineHeight: 1,
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "absolute", top: 10, right: 10, flexShrink: 0,
          fontFamily: "inherit",
        }}>
          {unreadCount > 99 ? "99+" : unreadCount}
        </Box>
      )}
    </Box>
  );
}

function ContactGroupRow({ group, activeId, onSelect, showLineBadge, nowMs }) {
  const { items } = group;
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (items.some((item) => String(item.id) === String(activeId))) {
      setExpanded(true);
    }
  }, [activeId, items]);

  if (items.length === 1) {
    return (
      <ConversationListRow
        item={items[0]}
        active={String(activeId) === String(items[0].id)}
        onClick={onSelect}
        showLineBadge={showLineBadge}
        nowMs={nowMs}
      />
    );
  }

  const repItem = items[0];
  const totalUnread = items.reduce((sum, item) => sum + Number(item?.unread_count || 0), 0);
  const isAnyActive = items.some((item) => String(activeId) === String(item.id));
  const customerName = repItem?.customer?.name || "Unknown";
  const preview = repItem?.last_message_preview || repItem?.subject || "No recent message";
  const activityAt = repItem?.last_activity_at || repItem?.last_message_at || repItem?.created_at;
  const rowChannel = String(repItem?.channel || "").toLowerCase();
  const emailProvider = rowChannel === "email" ? resolveConversationEmailProvider(repItem) : null;
  const channelIcon = rowChannel === "whatsapp"
    ? <WhatsAppIcon sx={{ fontSize: 11, color: C.whatsapp, flexShrink: 0 }} />
    : rowChannel === "voice"
    ? <CallOutlinedIcon sx={{ fontSize: 11, color: C.voice, flexShrink: 0 }} />
    : <EmailProviderIcon provider={emailProvider} size={11} sx={{ flexShrink: 0 }} />;
  const isHotClient = conversationHasHotSignal(repItem);
  const primaryQuoteBadge = getPrimaryConversationRowBadge(Array.isArray(repItem?.quote_badges) ? repItem.quote_badges : []);
  const whatsAppLine = rowChannel === "whatsapp" ? getWhatsAppLinePresentation(repItem) : null;

  const AV_PALETTES = [
    { bg: "#dcfce7", color: "#166534" },
    { bg: "#e0f2fe", color: "#0369a1" },
    { bg: "#fef3c7", color: "#92400e" },
    { bg: "#f3f4f6", color: "#6b7280" },
    { bg: "#ede9fe", color: "#5b21b6" },
  ];
  const pal = AV_PALETTES[(customerName.charCodeAt(0) || 0) % AV_PALETTES.length];
  const avBg = isAnyActive ? "#dcfce7" : pal.bg;
  const avColor = isAnyActive ? "#166534" : pal.color;

  const badgeStyle = (badge) => {
    if (badge.tone === "success") return { background: "#dcfce7", color: "#166534" };
    if (badge.tone === "warning") return { background: "#fef9c3", color: "#854d0e" };
    if (badge.tone === "info")    return { background: "#e0f2fe", color: "#0369a1" };
    if (badge.tone === "danger")  return { background: "#FEF2F2", color: "#DC2626" };
    return { background: "#f3f4f6", color: "#6b7280" };
  };
  const primaryQuoteBadgeStyle = primaryQuoteBadge ? badgeStyle(primaryQuoteBadge) : null;
  const showHotBadge = isHotClient && (!primaryQuoteBadge || !["danger", "warning"].includes(primaryQuoteBadge.tone));
  const baseRowSurface = isAnyActive ? "#F1FBF4" : "#FFFFFF";
  const hoverRowSurface = isAnyActive ? "#ECF8F0" : "#F8FBF8";

  return (
    <Box>
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          px: "12px", py: "9px",
          borderBottom: "1px solid #F1F4F2",
          cursor: "pointer", display: "flex", gap: "9px",
          position: "relative", bgcolor: baseRowSurface,
          borderLeft: isAnyActive
            ? "3px solid #16a34a"
            : whatsAppLine
              ? `3px solid ${alpha(whatsAppLine.accent, totalUnread ? 0.8 : 0.42)}`
              : isHotClient ? "3px solid #f97316"
              : totalUnread ? `3px solid ${C.whatsapp}`
              : "3px solid transparent",
          "&:hover": { bgcolor: hoverRowSurface },
          transition: "background-color 120ms ease, border-color 120ms ease",
          touchAction: "manipulation",
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        <Box sx={{
          width: 34, height: 34, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 700, flexShrink: 0, mt: "1px",
          bgcolor: avBg, color: avColor, fontFamily: "inherit",
        }}>
          {initials(customerName)}
        </Box>

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "4px" }}>
            <Box sx={{
              fontSize: 13, fontWeight: 700, color: "#111111",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
              display: "flex", alignItems: "center", gap: "5px", fontFamily: "inherit",
            }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{customerName}</span>
              <Box component="span" sx={{
                flexShrink: 0, fontSize: 8.5, fontWeight: 700,
                px: "5px", py: "1px", borderRadius: "10px",
                bgcolor: "#EFF3F0", color: "#3D6B4A", fontFamily: "inherit",
              }}>
                {items.length}
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "3px", flexShrink: 0 }}>
              <Box sx={{ color: "#BAC6BF", display: "inline-flex", alignItems: "center" }}>{channelIcon}</Box>
              <Box sx={{ fontSize: 10, color: "#9AA9A0", fontFamily: "inherit", fontWeight: 500 }}>
                {formatListTimestamp(activityAt)}
              </Box>
              <Box sx={{ color: "#9AA9A0", display: "inline-flex", ml: "1px" }}>
                {expanded
                  ? <ExpandLessRoundedIcon sx={{ fontSize: 14 }} />
                  : <ExpandMoreRoundedIcon sx={{ fontSize: 14 }} />}
              </Box>
            </Box>
          </Box>

          <Box sx={{
            fontSize: 11, color: "#7F8E86",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            maxWidth: "100%", mt: "3px", fontFamily: "inherit",
          }}>
            {preview}
          </Box>

          {(showHotBadge || primaryQuoteBadge || (showLineBadge && whatsAppLine)) && (
            <Box sx={{ display: "flex", gap: "3px", mt: "4px", flexWrap: "wrap", alignItems: "center" }}>
              {showLineBadge && whatsAppLine ? (
                <Box sx={{
                  fontSize: 8.5, fontWeight: 600, px: "6px", py: "1.5px", borderRadius: "20px",
                  background: alpha(whatsAppLine.accent, 0.06), color: alpha(whatsAppLine.color, 0.75),
                  fontFamily: "inherit", maxWidth: 120,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {whatsAppLine.label}
                </Box>
              ) : null}
              {showHotBadge && (
                <Box sx={{
                  fontSize: 8.5, fontWeight: 700, px: "6px", py: "1.5px", borderRadius: "20px",
                  background: "#FEF2F2", color: "#DC2626", fontFamily: "inherit",
                }}>
                  HOT
                </Box>
              )}
              {primaryQuoteBadge && primaryQuoteBadgeStyle ? (
                <Box sx={{
                  fontSize: 8.5, fontWeight: 600, px: "6px", py: "1.5px", borderRadius: "20px",
                  background: primaryQuoteBadgeStyle.background, color: primaryQuoteBadgeStyle.color,
                  fontFamily: "inherit",
                }}>
                  {primaryQuoteBadge.label}
                </Box>
              ) : null}
            </Box>
          )}
        </Box>

        {totalUnread > 0 && !isAnyActive && (
          <Box sx={{
            minWidth: 18, height: 18, px: "3px",
            borderRadius: "999px", bgcolor: C.whatsapp, color: "#FFFFFF",
            fontSize: 9, fontWeight: 800, lineHeight: 1,
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "absolute", top: 10, right: 10, flexShrink: 0,
            fontFamily: "inherit",
          }}>
            {totalUnread > 99 ? "99+" : totalUnread}
          </Box>
        )}
      </Box>

      {expanded && (
        <Box sx={{ borderLeft: "3px solid #E0EDE4", ml: "17px" }}>
          {items.map((item) => (
            <ConversationListRow
              key={item.id}
              item={item}
              active={String(activeId) === String(item.id)}
              onClick={onSelect}
              showLineBadge={false}
              nowMs={nowMs}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── Feature: Internal Collaboration Thread ───────────────────────────────────
function TeamNotesPanel({ conversationId, messages, onNoteAdded }) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const notes = (Array.isArray(messages) ? messages : []).filter(
    (m) => String(m?.message_type || "").toLowerCase() === "internal_note",
  );

  // Scroll to bottom when notes change
  useEffect(() => {
    if (expanded && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [notes.length, expanded]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await omnichannelInboxApi.createInternalNote(conversationId, text);
      setDraft("");
      onNoteAdded?.();
    } catch {
      // silent — main thread will show errors
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ border: "1px solid #efefef", borderRadius: "10px", overflow: "hidden", mx: "14px", mb: "12px", fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Header toggle */}
      <Box
        onClick={() => setExpanded((p) => !p)}
        sx={{
          px: "10px", py: "8px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          cursor: "pointer", bgcolor: "#fffbeb",
          borderBottom: expanded ? "1px solid #fef08a" : "none",
          userSelect: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 11, fontWeight: 600, color: "#92400e" }}>
          <NotesRoundedIcon sx={{ fontSize: 13 }} />
          Team Notes
          {notes.length > 0 && (
            <Box component="span" sx={{ background: "#fef08a", color: "#78350f", borderRadius: "10px", px: "6px", py: "1px", fontSize: 9, fontWeight: 700, ml: "2px" }}>
              {notes.length}
            </Box>
          )}
        </Box>
        {expanded ? <ExpandLessRoundedIcon sx={{ fontSize: 14, color: "#92400e" }} /> : <ExpandMoreRoundedIcon sx={{ fontSize: 14, color: "#92400e" }} />}
      </Box>

      {expanded && (
        <Box>
          {/* Notes list */}
          <Box ref={listRef} sx={{ maxHeight: 200, overflowY: "auto", p: "8px 10px", display: "flex", flexDirection: "column", gap: "7px", bgcolor: "#fefce8" }}>
            {notes.length === 0 ? (
              <Box sx={{ fontSize: 11, color: "#a3a3a3", textAlign: "center", py: "10px", fontStyle: "italic" }}>
                No team notes yet — add one below.
              </Box>
            ) : (
              notes.map((note) => (
                <Box
                  key={note.id}
                  sx={{
                    bgcolor: "#fffde7",
                    border: "1px solid #fde68a",
                    borderRadius: "8px",
                    p: "7px 10px",
                  }}
                >
                  <Box sx={{ fontSize: 10, fontWeight: 700, color: "#92400e", mb: "3px", display: "flex", justifyContent: "space-between", gap: "4px" }}>
                    <span>{note.creator?.name || "Team"}</span>
                    <span style={{ fontWeight: 400, color: "#a16207" }}>{formatListTimestamp(note.created_at || note.sent_at)}</span>
                  </Box>
                  <Box sx={{ fontSize: 12, color: "#292524", lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {note.body?.text || note.body || ""}
                  </Box>
                </Box>
              ))
            )}
          </Box>

          {/* Input row */}
          <Box sx={{ borderTop: "1px solid #fef08a", p: "6px 8px", display: "flex", gap: "6px", bgcolor: "#fffbeb" }}>
            <Box
              component="input"
              type="text"
              placeholder="Add a team note…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
              }}
              sx={{
                flex: 1, fontSize: 11,
                border: "1px solid #fde68a", borderRadius: "6px",
                px: "8px", py: "5px", outline: "none",
                fontFamily: "'Inter', system-ui, sans-serif",
                bgcolor: "#ffffff",
                "&:focus": { borderColor: "#d97706", boxShadow: "0 0 0 2px rgba(217,119,6,0.12)" },
              }}
            />
            <Box
              component="button"
              onClick={handleSend}
              disabled={!draft.trim() || sending}
              sx={{
                border: "none", borderRadius: "6px", px: "10px", py: "5px",
                bgcolor: "#d97706", color: "#fff",
                fontSize: 11, fontWeight: 600, cursor: "pointer",
                "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
                fontFamily: "'Inter', system-ui, sans-serif",
              }}
            >
              {sending ? "…" : "Add"}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function HeaderActionIcon({ title, onClick, disabled = false, children, color = C.subtext }) {
  const safeOnClick = typeof onClick === "function" ? onClick : undefined;

  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={safeOnClick}
          disabled={disabled || !safeOnClick}
          sx={{
            width: 30,
            height: 30,
            color,
            borderRadius: "8px",
            border: "none",
            bgcolor: "transparent",
            boxShadow: "none",
            "&:hover": {
              bgcolor: alpha(C.text, 0.07),
              color: C.text,
            },
            "&:active": {
              bgcolor: alpha(C.text, 0.11),
              transform: "scale(0.93)",
            },
            "&:disabled": { opacity: 0.3 },
            transition: "all 130ms ease",
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function ConversationHeaderMetaPill({ icon = null, label = "", accent = C.subtext, emphasized = false }) {
  const text = String(label || "").trim();

  if (!text) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.55,
        minWidth: 0,
        maxWidth: "100%",
        px: "9px",
        py: "4px",
        borderRadius: "8px",
        bgcolor: emphasized ? alpha(accent, 0.09) : alpha(accent, 0.04),
        color: emphasized ? accent : C.subtext,
        border: `1px solid ${alpha(accent, emphasized ? 0.2 : 0.1)}`,
        boxShadow: emphasized ? `0 1px 4px ${alpha(accent, 0.12)}` : "none",
      }}
    >
      {icon ? (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            color: emphasized ? accent : C.shellMuted,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      ) : null}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          fontSize: 11,
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          letterSpacing: "0.1px",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

function ConversationSectionLabel({ label, helper = "", color = C.text, tint = "#FBFCFB" }) {
  return (
    <Box
      sx={{
        px: "14px",
        pt: "9px",
        pb: "5px",
        bgcolor: tint === "#FBFCFB" ? "transparent" : tint,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "999px",
              bgcolor: color,
              boxShadow: `0 0 0 4px ${alpha(color, 0.12)}`,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color,
              fontWeight: 700,
              letterSpacing: 0.28,
              textTransform: "uppercase",
            }}
          >
            {label}
          </Typography>
        </Stack>
        {helper ? (
          <Box
            sx={{
              px: "6px",
              py: "1px",
              borderRadius: "999px",
              bgcolor: "#FFFFFF",
              border: `1px solid ${alpha(color, 0.14)}`,
              color,
              fontSize: 10,
              fontWeight: 600,
              lineHeight: 1.3,
              flexShrink: 0,
            }}
          >
            {helper}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}

function WhatsAppLineIdentityPanel({ line, coverageCount = 0 }) {
  if (!line) {
    return null;
  }

  return (
    <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
      <ConversationHeaderMetaPill
        label={line.detailLabel}
        accent={line.accent}
        emphasized
      />
      <ConversationHeaderMetaPill
        icon={<WhatsAppIcon sx={{ fontSize: 14 }} />}
        label={[line.providerLabel, line.phoneNumber].filter(Boolean).join(" · ")}
        accent={line.accent}
      />
      {line.scope === "consultant" && line.ownerName ? (
        <ConversationHeaderMetaPill
          icon={<AssignmentIndRoundedIcon sx={{ fontSize: 14 }} />}
          label={`Handled by ${line.ownerName}`}
          accent={line.accent}
        />
      ) : null}
      {line.scope === "consultant" && coverageCount > 0 ? (
        <ConversationHeaderMetaPill
          icon={<AllInboxRoundedIcon sx={{ fontSize: 14 }} />}
          label={`Coverage team ${coverageCount}`}
          accent={line.accent}
        />
      ) : null}
    </Stack>
  );
}

function InboxQuickFilterButton({ label, count = null, active, onClick, accent = C.whatsapp, icon = null }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        height: 32,
        px: count === null ? "12px" : "10px",
        borderRadius: "999px",
        border: "1px solid",
        borderColor: active ? alpha(accent, 0.24) : alpha(C.text, 0.08),
        bgcolor: active ? alpha(accent, 0.08) : "#FFFFFF",
        color: active ? accent : C.subtext,
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        fontSize: 11,
        fontWeight: 700,
        cursor: "pointer",
        transition: "all 140ms ease",
        "&:hover": {
          borderColor: alpha(accent, 0.2),
          bgcolor: alpha(accent, active ? 0.1 : 0.04),
        },
      }}
    >
      {icon ? <Box sx={{ display: "inline-flex", alignItems: "center", color: "inherit" }}>{icon}</Box> : null}
      <Box component="span">{label}</Box>
      {count !== null ? (
        <Box
          component="span"
          sx={{
            minWidth: 16,
            height: 16,
            px: count > 9 ? "4px" : "3px",
            borderRadius: "999px",
            bgcolor: active ? "#FFFFFF" : alpha(accent, 0.12),
            color: accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          {count}
        </Box>
      ) : null}
    </Box>
  );
}

function InboxChannelRailButton({ label, count, active, onClick, icon, accent = C.whatsapp }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        height: 42,
        px: "2px",
        borderRadius: 0,
        border: "none",
        borderBottom: "2px solid",
        borderColor: active ? accent : "transparent",
        bgcolor: "transparent",
        color: active ? C.text : C.subtext,
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        cursor: "pointer",
        transition: "all 140ms ease",
        "&:hover": {
          color: C.text,
        },
      }}
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", color: active ? accent : C.subtext }}>
        {icon}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 700, color: "inherit", fontSize: 13 }}>
        {label}
      </Typography>
      <Box
        sx={{
          minWidth: 18,
          height: 18,
          px: count > 9 ? "5px" : "4px",
          borderRadius: "999px",
          bgcolor: alpha(C.whatsapp, 0.12),
          color: C.whatsapp,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 10,
          fontWeight: 800,
          lineHeight: 1,
        }}
      >
        {count}
      </Box>
    </Box>
  );
}

function InboxWelcomeState({
  todayNewCount,
  todayTouchedCount,
  todayAwaitingCount,
  todayOverdueCount,
  onViewUnassigned,
  onOpenDrafts,
  onStartConversation,
  onViewHotLeads,
}) {
  const quickActions = [
    {
      label: "View unassigned",
      helper: "See conversations needing attention",
      onClick: onViewUnassigned,
      icon: <SearchRoundedIcon sx={{ fontSize: 18 }} />,
    },
    {
      label: "Open recent drafts",
      helper: "Continue where you left off",
      onClick: onOpenDrafts,
      icon: <TextSnippetRoundedIcon sx={{ fontSize: 18 }} />,
    },
  ];

  const overviewHeadline = todayNewCount > 0
    ? `${todayNewCount} new conversation${todayNewCount === 1 ? "" : "s"} added today`
    : todayTouchedCount > 0
      ? `${todayTouchedCount} conversation${todayTouchedCount === 1 ? "" : "s"} active today`
      : "No new conversations yet today";

  const overviewBits = [
    `${todayTouchedCount} active today`,
    `${todayAwaitingCount} awaiting`,
  ];

  if (todayOverdueCount > 0) {
    overviewBits.push(`${todayOverdueCount} overdue`);
  }

  const overviewHelper = overviewBits.join(" • ");
  const overviewAction = todayAwaitingCount > 0 || todayOverdueCount > 0 ? onViewUnassigned : onViewHotLeads;

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        px: { xs: 2, md: 3 },
        py: { xs: 3, md: 4 },
        background:
          "radial-gradient(circle at 16% 20%, rgba(78,198,102,0.08) 0%, transparent 22%), radial-gradient(circle at 88% 18%, rgba(46,184,56,0.10) 0%, transparent 25%), linear-gradient(180deg, #FFFFFF 0%, #F8FBF8 100%)",
        position: "relative",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "8% -6% auto auto",
          width: 360,
          height: 260,
          background: "radial-gradient(circle at 40% 38%, rgba(72,190,110,0.10) 0%, rgba(72,190,110,0.02) 56%, transparent 72%)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: "auto -8% 18% auto",
          width: 430,
          height: 180,
          background: "radial-gradient(circle at 22% 50%, rgba(45,184,56,0.09) 0%, rgba(45,184,56,0.03) 44%, transparent 72%)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 54,
          right: 20,
          width: 110,
          height: 150,
          opacity: 0.35,
          backgroundImage: `radial-gradient(${alpha(C.whatsapp, 0.42)} 1.2px, transparent 1.2px)`,
          backgroundSize: "12px 12px",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: -60,
          top: 110,
          width: "120%",
          height: 180,
          background: "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(232,248,236,0.32) 44%, rgba(255,255,255,0) 100%)",
          borderRadius: "50%",
          transform: "rotate(-8deg)",
          pointerEvents: "none",
        }}
      />

      <Stack spacing={2} sx={{ maxWidth: 422, mx: "auto", mt: { xs: 2.5, md: 3 }, pb: { xs: 8, md: 8 }, position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              width: 54, height: 54, borderRadius: "18px",
              bgcolor: alpha(C.whatsapp, 0.10), color: C.whatsapp,
              display: "flex", alignItems: "center", justifyContent: "center",
              mx: "auto", mb: 1.4,
            }}
          >
            <AllInboxRoundedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: C.text, fontSize: "1.1rem" }}>
            No conversation selected
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.6, color: C.subtext, maxWidth: 280, mx: "auto", fontSize: 12 }}>
            Select a conversation from the left to get started.
          </Typography>
        </Box>

        {/* Today overview — shown first so it's always visible without scrolling */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: `1px solid ${alpha(C.text, 0.08)}`,
            px: 2,
            py: 1.5,
            bgcolor: "#FFFFFF",
            boxShadow: "0 12px 28px -22px rgba(15,23,42,0.14)",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: C.text, mb: 1, fontSize: 12 }}>
            Today overview
          </Typography>
          {/* Mini stat row */}
          <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
            {[
              { label: "Active", value: todayTouchedCount, color: C.whatsapp },
              { label: "Awaiting", value: todayAwaitingCount, color: "#B45309" },
              { label: "Overdue", value: todayOverdueCount, color: "#DC2626" },
            ].map(({ label, value, color }) => (
              <Box
                key={label}
                sx={{
                  flex: 1, textAlign: "center", py: "6px", borderRadius: "10px",
                  bgcolor: alpha(color, 0.06), border: `1px solid ${alpha(color, 0.1)}`,
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
                <Typography sx={{ fontSize: 9, color, mt: 0.3, fontWeight: 600 }}>{label}</Typography>
              </Box>
            ))}
          </Stack>
          <Box
            component="button"
            onClick={overviewAction}
            sx={{
              width: "100%", p: "9px 12px", borderRadius: "12px",
              border: `1px solid ${alpha(C.text, 0.07)}`, bgcolor: "#FFFFFF",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              textAlign: "left", cursor: "pointer",
              "&:hover": { bgcolor: "#F8FBF8" },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: C.text, fontSize: 12 }}>
                {overviewHeadline}
              </Typography>
              <Typography variant="caption" sx={{ color: C.subtext, fontSize: 11 }}>
                {overviewHelper}
              </Typography>
            </Box>
            <ArrowForwardRoundedIcon sx={{ fontSize: 16, color: C.subtext, flexShrink: 0 }} />
          </Box>
        </Paper>

        {/* Quick actions */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: `1px solid ${alpha(C.text, 0.08)}`,
            px: 2,
            py: 1.5,
            bgcolor: "#FFFFFF",
            boxShadow: "0 12px 28px -22px rgba(15,23,42,0.12)",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: C.text, mb: 0.9, fontSize: 12 }}>
            Quick actions
          </Typography>
          <Stack spacing={0.7}>
            {quickActions.map((action) => (
              <Box
                key={action.label}
                component="button"
                onClick={action.onClick}
                sx={{
                  width: "100%",
                  p: "9px 10px",
                  borderRadius: "12px",
                  border: "none",
                  bgcolor: "#FAFBFA",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textAlign: "left",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#F2F7F3" },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 30, height: 30, borderRadius: "10px",
                      bgcolor: alpha(C.whatsapp, 0.10), color: C.whatsapp,
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}
                  >
                    {action.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: C.text, fontSize: 12 }}>
                      {action.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: C.subtext, fontSize: 11 }}>
                      {action.helper}
                    </Typography>
                  </Box>
                </Stack>
                <ArrowForwardRoundedIcon sx={{ fontSize: 16, color: C.subtext, flexShrink: 0 }} />
              </Box>
            ))}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 1.1 }}>
            <Button
              variant="contained"
              onClick={onStartConversation}
              startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                flex: "0 1 auto",
                minHeight: 38,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 800,
                boxShadow: "none",
                fontSize: 12,
                px: 1.8,
              }}
            >
              Start new conversation
            </Button>
            <Button
              variant="text"
              onClick={onViewHotLeads}
              endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: C.text,
                fontSize: 12,
                minWidth: 108,
                whiteSpace: "nowrap",
              }}
            >
              View HOT leads
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}

export default function UnifiedInboxMessagingPage({
  initialChannel = "",
  initialQ = "",
  initialConversationId = "",
  initialConversationChannel = "",
  channelLocked = false,
  title = "Unified Inbox",
  eyebrow = "Unified Inbox",
  description = "A cleaner unified messaging workspace for chats and email threads.",
  shellExpandedSidebar = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const chatInputRef = useRef(null);
  const emailBodyRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const composerAttachmentsRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const mediaRecorderStreamRef = useRef(null);
  const mediaRecorderChunksRef = useRef([]);
  const shouldPersistRecordingRef = useRef(false);
  const mountedRef = useRef(true);
  const appliedFiltersRef = useRef(makeFilters(initialChannel));
  const currentChannelRef = useRef(initialChannel || "");
  const activeIdRef = useRef(null);
  const listRequestRef = useRef(0);
  const todayOverviewRequestRef = useRef(0);
  const detailRequestRef = useRef(0);
  const listSnapshotRef = useRef("[]");
  const detailSnapshotRef = useRef(
    conversationDetailSnapshot(null, [], [])
  );
  const searchDebounceRef = useRef(null);
  const previousActiveConversationIdRef = useRef(null);
  const liveSyncQueueRef = useRef(Promise.resolve());
  const restoredDraftVersionRef = useRef({});
  const linkedSelectionSuppressedRef = useRef(false);
  const linkedSeedKeyRef = useRef(`${initialConversationId || ""}:${initialConversationChannel || ""}`);

  const [draftFilters, setDraftFilters] = useState(() => ({ ...makeFilters(initialChannel), q: initialQ || "" }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...makeFilters(initialChannel), q: initialQ || "" }));
  const [items, setItems] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [whatsappLines, setWhatsappLines] = useState([]);
  const [workstreams, setWorkstreams] = useState([]);
  const [todayOverview, setTodayOverview] = useState(EMPTY_TODAY_OVERVIEW);
  const [gmailMailboxStatus, setGmailMailboxStatus] = useState(null);
  const [gmailMailboxLoading, setGmailMailboxLoading] = useState(false);
  const [gmailMailboxSyncing, setGmailMailboxSyncing] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [activeConversation, setActiveConversation] = useState(null);
  const [detailMessages, setDetailMessages] = useState([]);
  const [detailVoiceCalls, setDetailVoiceCalls] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [linkedPresenceUserId, setLinkedPresenceUserId] = useState(null);
  const [linkedPresenceSnapshot, setLinkedPresenceSnapshot] = useState(null);
  const [activityNowMs, setActivityNowMs] = useState(() => Date.now());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [quoteQueueOpen, setQuoteQueueOpen] = useState(false);
  const [quickListFilter, setQuickListFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("recent");
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [showConversationDetails, setShowConversationDetails] = useState(true);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState(null);
  const [cannedReplies, setCannedReplies] = useState([]);
  const [cannedRepliesLoading, setCannedRepliesLoading] = useState(false);
  const [chatComposer, setChatComposer] = useState("");
  const [emailComposer, setEmailComposer] = useState({
    to: "",
    cc: "",
    bcc: "",
    showCcBcc: false,
    subject: "",
    body: "",
  });
  const [emailComposerExpanded, setEmailComposerExpanded] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [composerAttachments, setComposerAttachments] = useState([]);
  const [recordingAudio, setRecordingAudio] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [statusSaving, setStatusSaving] = useState(false);
  const [dialogs, setDialogs] = useState({
    assign: false,
    linkInquiry: false,
    note: false,
    followUp: false,
    liveCall: false,
    voiceLog: false,
  });
  const [newConversationDialogOpen, setNewConversationDialogOpen] = useState(false);
  const [newConversationDraft, setNewConversationDraft] = useState(() => createNewConversationDraft());
  const [startingConversation, setStartingConversation] = useState(false);
  const [pendingWelcomeAction, setPendingWelcomeAction] = useState(null);
  const [snackbar, setSnackbar] = useState("");
  const [ownershipWarning, setOwnershipWarning] = useState(null); // { message, newOwner }
  const [claimingConversation, setClaimingConversation] = useState(false);
  const [composerLocked, setComposerLocked] = useState(false); // locked by someone else
  const [pendingRestoredDraft, setPendingRestoredDraft] = useState(null);
  const [conversationPresence, setConversationPresence] = useState({
    assigned_to: null,
    being_viewed_by: [],
    being_edited_by: null,
  });
  const [managerOverrideArmed, setManagerOverrideArmed] = useState(false);
  // Number of messages visible in the thread view. Starts at 40; grows by 40
  // each time the user clicks "Load older messages". Reset on conversation change.
  const [msgWindowSize, setMsgWindowSize] = useState(40);
  const [seededConversationId, setSeededConversationId] = useState(() => String(initialConversationId || ""));
  const [seededConversationChannel, setSeededConversationChannel] = useState(() => String(initialConversationChannel || ""));
  const inboxQuery = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const linkedConversationId = String(inboxQuery.get("conversation") || seededConversationId || "");
  const linkedChannel = String(inboxQuery.get("channel") || seededConversationChannel || "");

  const currentChannel = channelLocked ? initialChannel : appliedFilters.channel;
  const titleLabel = title === "Unified Inbox" ? "Messages" : title;
  const activeMeta = channelMeta(activeConversation?.channel || currentChannel || "", activeConversation);
  const isWhatsAppConversation = String(activeConversation?.channel || "").toLowerCase() === "whatsapp";
  const isEmailConversation = String(activeConversation?.channel || "").toLowerCase() === "email";
  const isVoiceConversation = String(activeConversation?.channel || "").toLowerCase() === "voice";
  const isChatConversation = Boolean(activeConversation) && !isEmailConversation && !isVoiceConversation;
  const hasQuoteDetails = Boolean(
    activeConversation?.quote_context?.show_quote_ui
    || activeConversation?.active_quote
  );
  const hasCustomerMessageInsights = !isVoiceConversation && detailMessages.some(
    (message) =>
      message?.direction === "inbound"
      && !["system", "internal_note"].includes(String(message?.message_type || "").toLowerCase())
      && message?.body?.text
  );
  const showConversationDetailsToggle = Boolean(activeConversation) && (hasQuoteDetails || hasCustomerMessageInsights);
  const railValue = channelLocked ? initialChannel : draftFilters.channel;
  const listChannel = String(currentChannel || railValue || "").toLowerCase();
  const isWhatsAppMode = listChannel === "whatsapp";
  const isWhatsAppFilterContext = String((channelLocked ? initialChannel : draftFilters.channel) || currentChannel || "").toLowerCase() === "whatsapp";
  const showGmailMailboxCard = String(railValue || currentChannel || "") === "email"
    || isEmailConversation
    || (gmailMailboxStatus !== null && !gmailMailboxStatus?.connected);
  const consultantWhatsappLines = useMemo(
    () =>
      Array.isArray(whatsappLines)
        ? whatsappLines.filter((line) => String(line?.line_type || "").toLowerCase() === "consultant")
        : [],
    [whatsappLines]
  );
  const filterableWhatsappLines = useMemo(
    () => (Array.isArray(whatsappLines) ? whatsappLines.filter((line) => Boolean(line?.id)) : []),
    [whatsappLines]
  );
  const launchableWhatsappLines = useMemo(
    () => (Array.isArray(whatsappLines) ? whatsappLines.filter((line) => Boolean(line?.id)) : []),
    [whatsappLines]
  );
  const visibleItems = useMemo(() => {
    let filtered;
    switch (quickListFilter) {
      case "unread":
        filtered = items.filter((item) => Number(item?.unread_count || 0) > 0);
        break;
      case "hot":
        filtered = items.filter((item) => conversationHasHotSignal(item));
        break;
      default:
        filtered = items;
    }
    // Apply sort override (default "recent" is already applied by sortConversationsByActivity on fetch)
    if (sortOrder === "oldest") {
      return [...filtered].sort((a, b) => toConversationActivityMs(a) - toConversationActivityMs(b));
    }
    if (sortOrder === "unread_first") {
      return [...filtered].sort((a, b) => {
        const ua = Number(a?.unread_count || 0);
        const ub = Number(b?.unread_count || 0);
        if (ub !== ua) return ub - ua;
        return toConversationActivityMs(b) - toConversationActivityMs(a);
      });
    }
    if (sortOrder === "name_az") {
      return [...filtered].sort((a, b) => {
        const na = String(a?.customer?.name || "").toLowerCase();
        const nb = String(b?.customer?.name || "").toLowerCase();
        return na < nb ? -1 : na > nb ? 1 : 0;
      });
    }
    return filtered; // "recent" — already sorted by fetch pipeline
  }, [items, quickListFilter, sortOrder]);
  const showWhatsAppLineSections = isWhatsAppMode && !appliedFilters.whatsappLineScope && !appliedFilters.whatsappLineId;
  const broadcastLineItems = useMemo(
    () => (showWhatsAppLineSections ? visibleItems.filter((item) => getWhatsAppLineScope(item) === "broadcast") : []),
    [visibleItems, showWhatsAppLineSections]
  );
  const rotatedWorkLineItems = useMemo(
    () => (showWhatsAppLineSections ? visibleItems.filter((item) => getWhatsAppLineScope(item) === "rotated_work") : []),
    [visibleItems, showWhatsAppLineSections]
  );
  const consultantLineItems = useMemo(
    () => (showWhatsAppLineSections ? visibleItems.filter((item) => getWhatsAppLineScope(item) === "consultant") : []),
    [visibleItems, showWhatsAppLineSections]
  );
  const broadcastWhatsappThreadCount = useMemo(
    () => items.filter((item) => getWhatsAppLineScope(item) === "broadcast").length,
    [items]
  );
  const rotatedWorkWhatsappThreadCount = useMemo(
    () => items.filter((item) => getWhatsAppLineScope(item) === "rotated_work").length,
    [items]
  );
  const consultantWhatsappThreadCount = useMemo(
    () => items.filter((item) => getWhatsAppLineScope(item) === "consultant").length,
    [items]
  );
  const activeWhatsAppLine = isWhatsAppConversation ? getWhatsAppLinePresentation(activeConversation) : null;
  const activeWhatsAppCoverageCount = Array.isArray(activeConversation?.whatsapp_line?.members)
    ? activeConversation.whatsapp_line.members.filter((member) => Boolean(member?.is_active)).length
    : 0;
  const activeCustomerAddress = useMemo(() => {
    if (!activeConversation) {
      return "";
    }

    if (isEmailConversation) {
      return String(
        activeConversation?.customer?.email
        || activeConversation?.contact?.email
        || activeConversation?.customer?.phone
        || activeConversation?.contact?.phone
        || ""
      ).trim();
    }

    return String(
      activeConversation?.customer?.phone
      || activeConversation?.contact?.phone
      || activeConversation?.customer?.email
      || activeConversation?.contact?.email
      || ""
    ).trim();
  }, [activeConversation, isEmailConversation]);
  const activeConversationSubject = String(activeConversation?.subject || "").trim();
  const supportWorkstream = useMemo(
    () =>
      Array.isArray(workstreams)
        ? workstreams.find((workstream) => String(workstream?.name || "").toLowerCase().includes("support"))
        : null,
    [workstreams]
  );
  const activeInquiryWorkspaceId = activeConversation?.inquiry?.workspace?.firestore_document_id || null;
  const uiFlags = activeConversation?.ui_flags || {};
  const contactRollups = activeConversation?.contact_rollups || null;
  const customerJourney = activeConversation?.customer_journey || null;
  const quoteBehavior = activeConversation?.quote_behavior || null;
  const nextBestAction = activeConversation?.next_best_action || null;
  const showContactRollupsUi = Boolean(uiFlags?.contact_rollups_ui);
  const showIdentityReviewUi = Boolean(uiFlags?.identity_review_ui);
  const showJourneyLabelsUi = Boolean(uiFlags?.journey_labels_ui);
  const showQuoteBehaviorUi = Boolean(uiFlags?.quote_behavior_ui);
  const showNextBestActionUi = Boolean(uiFlags?.next_best_action_ui);
  const unreadConversationsCount = items.reduce((sum, item) => sum + Number(item?.unread_count || 0), 0);
  const trackedQuotesCount = items.filter((item) => Array.isArray(item?.quote_badges) && item.quote_badges.length > 0).length;
  const hotLeadCount = items.filter((item) => conversationHasHotSignal(item)).length;
  const supportConversationCount = items.filter((item) =>
    String(item?.workstream?.name || "").toLowerCase().includes("support")
  ).length;
  const emailConversationCount = items.filter((item) => String(item?.channel || "").toLowerCase() === "email").length;
  const headerJourneyChip = showJourneyLabelsUi && customerJourney?.primary_label
    ? {
        key: "journey",
        ...journeyChipConfig(customerJourney.primary_label),
      }
    : null;
  const headerQuoteBehaviorChip = showQuoteBehaviorUi && quoteBehavior?.primary_label
    ? {
        key: "quote_behavior",
        ...quoteBehaviorChipConfig(quoteBehavior.primary_label),
      }
    : null;
  const headerNextBestActionChip = showNextBestActionUi && nextBestAction?.action_code
    ? {
        key: "next_best_action",
        ...nextBestActionChipConfig(nextBestAction),
      }
    : null;
  const headerOwnershipChip = isWhatsAppConversation
    ? ownershipSourceChipConfig(activeConversation?.ownership)
    : null;
  const headerOwnershipContextChips = [
    isWhatsAppConversation && activeConversation?.ownership?.campaign?.name
      ? {
          key: "ownership_campaign",
          label: `Campaign: ${activeConversation.ownership.campaign.name}`,
          tone: "neutral",
        }
      : null,
    isWhatsAppConversation && activeConversation?.ownership?.fallback_used
      ? {
          key: "ownership_fallback",
          label: "Fallback used",
          tone: "warning",
        }
      : null,
    isWhatsAppConversation && activeConversation?.whatsapp_line?.meta_mode === "coexistence"
      ? {
          key: "coexistence_active",
          label: "Coexistence active",
          tone: "info",
        }
      : null,
  ].filter(Boolean);
  const headerCustomerChips = [
    showContactRollupsUi && contactRollups?.repeat_customer
      ? { key: "repeat", label: "Repeat customer", tone: "success" }
      : null,
    showContactRollupsUi && contactRollups?.multi_channel_customer
      ? { key: "multi", label: "Multi-channel", tone: "info" }
      : null,
    showIdentityReviewUi && Number(contactRollups?.possible_matches_awaiting_review_count || 0) > 0
      ? {
          key: "review",
          label: `${contactRollups.possible_matches_awaiting_review_count} review${Number(contactRollups.possible_matches_awaiting_review_count) === 1 ? "" : "s"}`,
          tone: "warning",
        }
      : null,
    showIdentityReviewUi && Number(contactRollups?.unresolved_identity_conflicts_count || 0) > 0
      ? {
          key: "conflict",
          label: `${contactRollups.unresolved_identity_conflicts_count} conflict${Number(contactRollups.unresolved_identity_conflicts_count) === 1 ? "" : "s"}`,
          tone: "danger",
        }
      : null,
  ].filter(Boolean);
  const customerActivityStatus = activeConversation && !isEmailConversation
    ? deriveCustomerActivityStatus({
        conversation: activeConversation,
        messages: detailMessages,
        presenceSnapshot: linkedPresenceSnapshot,
        inquiryWorkspaceId: activeInquiryWorkspaceId,
        nowMs: activityNowMs,
      })
    : null;
  const syncInboxLocation = useCallback((conversationId = null, channel = "") => {
    const params = new URLSearchParams(location.search);

    if (conversationId) {
      params.set("conversation", String(conversationId));
    } else {
      params.delete("conversation");
    }

    if (channel) {
      params.set("channel", String(channel));
    } else {
      params.delete("channel");
    }

    const nextSearch = params.toString();
    const currentSearch = location.search.startsWith("?") ? location.search.slice(1) : location.search;

    if (nextSearch === currentSearch) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const clearConversationLaunchSeed = useCallback(() => {
    setSeededConversationId("");
    setSeededConversationChannel("");
  }, []);

  const handleReturnToConversationList = useCallback(() => {
    linkedSelectionSuppressedRef.current = true;
    clearConversationLaunchSeed();
    syncInboxLocation(null, currentChannel || activeConversation?.channel || "");
    setDetailError("");
    setOwnershipWarning(null);
    setManagerOverrideArmed(false);
    setComposerLocked(false);
    setShowConversationDetails(false);
    setActiveConversation(null);
    setDetailMessages([]);
    setDetailVoiceCalls([]);
    setActiveId(null);
  }, [activeConversation?.channel, clearConversationLaunchSeed, currentChannel, syncInboxLocation]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
    currentChannelRef.current = currentChannel;
  }, [appliedFilters, currentChannel]);

  useEffect(() => {
    const nextSeedKey = `${initialConversationId || ""}:${initialConversationChannel || ""}`;

    if (linkedSeedKeyRef.current === nextSeedKey) {
      return;
    }

    linkedSeedKeyRef.current = nextSeedKey;
    linkedSelectionSuppressedRef.current = false;
    setSeededConversationId(String(initialConversationId || ""));
    setSeededConversationChannel(String(initialConversationChannel || ""));
  }, [initialConversationChannel, initialConversationId]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActivityNowMs(Date.now());
    }, 30000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!activeInquiryWorkspaceId) {
      setLinkedPresenceUserId(null);
      setLinkedPresenceSnapshot(null);
      return undefined;
    }

    const inquiryRef = doc(firestoreDb, "allInquiries", String(activeInquiryWorkspaceId));

    const unsubscribe = onSnapshot(
      inquiryRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setLinkedPresenceUserId(null);
          setLinkedPresenceSnapshot(null);
          return;
        }

        const nextUserId = snapshot.data()?.userId;
        setLinkedPresenceUserId(nextUserId ? String(nextUserId) : null);
      },
      () => {
        setLinkedPresenceUserId(null);
        setLinkedPresenceSnapshot(null);
      }
    );

    return () => unsubscribe();
  }, [activeInquiryWorkspaceId]);

  useEffect(() => {
    if (!linkedPresenceUserId) {
      setLinkedPresenceSnapshot(null);
      return undefined;
    }

    const presenceRef = doc(firestoreDb, "userPresence", String(linkedPresenceUserId));

    const unsubscribe = onSnapshot(
      presenceRef,
      (snapshot) => {
        setLinkedPresenceSnapshot(snapshot.exists() ? snapshot.data() : null);
      },
      () => {
        setLinkedPresenceSnapshot(null);
      }
    );

    return () => unsubscribe();
  }, [linkedPresenceUserId]);

  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  useEffect(() => {
    listSnapshotRef.current = conversationListSnapshot(items);
  }, [items]);

  useEffect(() => {
    detailSnapshotRef.current = conversationDetailSnapshot(
      activeConversation,
      detailMessages,
      detailVoiceCalls
    );
  }, [activeConversation, detailMessages, detailVoiceCalls]);

  useEffect(() => {
    composerAttachmentsRef.current = composerAttachments;
  }, [composerAttachments]);

  const loadGmailMailboxStatus = useCallback(async ({ showLoader = false } = {}) => {
    if (showLoader) {
      setGmailMailboxLoading(true);
    }

    try {
      const response = await omnichannelInboxApi.getGmailInboxStatus();

      if (!mountedRef.current) return;

      setGmailMailboxStatus(response || null);
    } catch (error) {
      if (!mountedRef.current) return;

      setGmailMailboxStatus(null);
    } finally {
      if (showLoader && mountedRef.current) {
        setGmailMailboxLoading(false);
      }
    }
  }, []);

  const syncTodayOverview = useCallback(async () => {
    const requestId = ++todayOverviewRequestRef.current;

    try {
      const filters = appliedFiltersRef.current;
      const response = await omnichannelInboxApi.getConversationSummary({
        channel: currentChannelRef.current || undefined,
        whatsapp_line_scope: filters.whatsappLineScope || undefined,
        whatsapp_line_id: filters.whatsappLineId || undefined,
      });

      if (!mountedRef.current || requestId !== todayOverviewRequestRef.current) return;

      setTodayOverview({
        ...EMPTY_TODAY_OVERVIEW,
        ...(response?.data?.today || {}),
      });
    } catch (_) {
      if (!mountedRef.current || requestId !== todayOverviewRequestRef.current) return;

      setTodayOverview(EMPTY_TODAY_OVERVIEW);
    }
  }, []);

  const syncConversationList = useCallback(async ({ showLoader = false } = {}) => {
    const requestId = ++listRequestRef.current;

    if (showLoader) {
      setListLoading(true);
      setListError("");
    }

    try {
      const filters = appliedFiltersRef.current;
      const response = await omnichannelInboxApi.listConversations({
        q: filters.q,
        assigned: filters.assigned,
        status: filters.status,
        priority: filters.priority,
        channel: currentChannelRef.current,
        workstream: filters.group,
        whatsapp_line_scope: filters.whatsappLineScope,
        whatsapp_line_id: filters.whatsappLineId,
        created_window: filters.created,
        closed_window: filters.closedAt,
        per_page: 100,
      });

      if (!mountedRef.current || requestId !== listRequestRef.current) return;

      const nextItems = sortConversationsByActivity(Array.isArray(response?.data) ? response.data : []);
      const nextSnapshot = conversationListSnapshot(nextItems);

      if (nextSnapshot !== listSnapshotRef.current) {
        listSnapshotRef.current = nextSnapshot;
        setItems(nextItems);
      }

      setListError("");
    } catch (error) {
      if (!mountedRef.current || requestId !== listRequestRef.current) return;

      if (showLoader) {
        setListError(error?.response?.data?.message || error?.message || "Failed to load conversations.");
        setItems([]);
      }
    } finally {
      if (showLoader && mountedRef.current) {
        setListLoading(false);
      }
    }
  }, []);

  const syncConversationDetail = useCallback(async (conversationId, { showLoader = false } = {}) => {
    const resolvedConversationId = conversationId ?? activeIdRef.current;
    if (!resolvedConversationId) return;
    const requestId = ++detailRequestRef.current;

    if (showLoader) {
      setDetailLoading(true);
      setDetailError("");
    }

    try {
      const response = await omnichannelInboxApi.getConversation(resolvedConversationId);
      const payload = unwrapDetailPayload(response);

      if (!mountedRef.current || requestId !== detailRequestRef.current || !payload?.conversation) return;

      const nextMessages = sortMessagesChronologically(Array.isArray(payload.messages) ? payload.messages : []);
      const nextVoiceCalls = Array.isArray(payload.voice_calls) ? payload.voice_calls : [];
      const nextDetailSnapshot = conversationDetailSnapshot(
        payload.conversation,
        nextMessages,
        nextVoiceCalls
      );

      if (nextDetailSnapshot !== detailSnapshotRef.current) {
        detailSnapshotRef.current = nextDetailSnapshot;
        setActiveConversation(payload.conversation);
        setDetailMessages(nextMessages);
        setDetailVoiceCalls(nextVoiceCalls);
      }

      setDetailError("");
      setItems((prev) => {
        const nextItems = prev.some((entry) => String(entry.id) === String(payload.conversation.id))
          ? prev.map((entry) =>
              String(entry.id) === String(payload.conversation.id)
                ? { ...entry, ...payload.conversation, unread_count: 0 }
                : entry
            )
          : [{ ...payload.conversation, unread_count: 0 }, ...prev];

        const sortedItems = sortConversationsByActivity(nextItems);
        const nextSnapshot = conversationListSnapshot(sortedItems);

        if (nextSnapshot !== listSnapshotRef.current) {
          listSnapshotRef.current = nextSnapshot;
          return sortedItems;
        }

        return prev;
      });

      const draftVersion = payload?.draft?.updated_at || payload?.draft?.id || null;
      if (payload?.draft && draftVersion && restoredDraftVersionRef.current[payload.conversation.id] !== draftVersion) {
        restoredDraftVersionRef.current = {
          ...restoredDraftVersionRef.current,
          [payload.conversation.id]: draftVersion,
        };
        setPendingRestoredDraft({
          conversationId: payload.conversation.id,
          channel: payload.conversation.channel,
          draft: payload.draft,
        });
        try {
          localStorage.removeItem(`draft_${payload.conversation.id}`);
        } catch (_) {
          // ignore local storage failures
        }
      }
    } catch (error) {
      if (!mountedRef.current || requestId !== detailRequestRef.current) return;

      if (showLoader) {
        setDetailError(error?.response?.data?.message || error?.message || "Failed to open conversation.");
      }
    } finally {
      if (showLoader && mountedRef.current && requestId === detailRequestRef.current) {
        setDetailLoading(false);
      }
    }
  }, []);

  const openConversationForViewing = useCallback(async (conversationId, { showLoader = false } = {}) => {
    if (!conversationId) return;

    setClaimingConversation(true);
    setManagerOverrideArmed(false);
    setComposerLocked(false);
    setOwnershipWarning(null);

    try {
      // Opening a conversation is view-only — no automatic claim.
      // Members must explicitly click "Claim" to take ownership.
      await syncConversationDetail(conversationId, { showLoader });
    } catch (error) {
      const errData = error?.response?.data;

      if (error?.response?.status === 409) {
        const fallbackOwner = errData?.claimant || errData?.state?.assigned_to || null;
        setOwnershipWarning({
          message: errData?.message || `Claimed by ${fallbackOwner?.name || "another consultant"} while you were opening this conversation.`,
          newOwner: fallbackOwner,
          claimConflict: true,
        });
        setComposerLocked(true);
        setDetailMessages([]);
        setDetailVoiceCalls([]);
        setDetailError(errData?.message || "This conversation was claimed by another consultant.");

        setActiveConversation((prev) => {
          if (!prev || String(prev.id) !== String(conversationId)) {
            return prev;
          }

          return {
            ...prev,
            assignment: {
              ...(prev.assignment || {}),
              assigned_to: errData?.state?.assigned_to || fallbackOwner,
              state: errData?.state?.assignment_state || prev.assignment?.state,
            },
            lock: errData?.state?.lock || prev.lock,
          };
        });

        return;
      }

      if (showLoader) {
        setDetailError(errData?.message || error?.message || "Failed to open conversation.");
      }
    } finally {
      setClaimingConversation(false);
    }
  }, [syncConversationDetail]);

  useEffect(() => {
    if (!channelLocked) return;

    setDraftFilters((prev) => withChannelFilter(prev, initialChannel || ""));
    setAppliedFilters((prev) => withChannelFilter(prev, initialChannel || ""));
  }, [channelLocked, initialChannel]);

  // Always load Gmail status on mount so we can show the connect prompt even on "All" tab.
  useEffect(() => {
    loadGmailMailboxStatus({ showLoader: showGmailMailboxCard });
  }, [loadGmailMailboxStatus, showGmailMailboxCard]);

  useEffect(() => {
    syncTodayOverview();
  }, [
    appliedFilters.channel,
    appliedFilters.whatsappLineId,
    appliedFilters.whatsappLineScope,
    currentChannel,
    syncTodayOverview,
  ]);

  useEffect(() => {
    syncConversationList({ showLoader: true });
  }, [
    appliedFilters.assigned,
    appliedFilters.channel,
    appliedFilters.closedAt,
    appliedFilters.created,
    appliedFilters.group,
    appliedFilters.priority,
    appliedFilters.q,
    appliedFilters.status,
    appliedFilters.whatsappLineId,
    appliedFilters.whatsappLineScope,
    currentChannel,
    syncConversationList,
  ]);

  useEffect(() => {
    let cancelled = false;

    listWhatsAppLines()
      .then((data) => {
        if (cancelled) return;
        setWhatsappLines(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setWhatsappLines([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    omnichannelInboxApi
      .listWorkstreams()
      .then((response) => {
        if (cancelled) return;
        setWorkstreams(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setWorkstreams([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (channelLocked || !linkedChannel) return;

    setDraftFilters((prev) => (prev.channel === linkedChannel ? prev : withChannelFilter(prev, linkedChannel)));
    setAppliedFilters((prev) => (prev.channel === linkedChannel ? prev : withChannelFilter(prev, linkedChannel)));
  }, [channelLocked, linkedChannel]);

  useEffect(() => {
    if (linkedSelectionSuppressedRef.current) {
      if (!linkedConversationId) {
        linkedSelectionSuppressedRef.current = false;
      }

      return;
    }

    if (!linkedConversationId) return;
    if (String(activeId || "") === String(linkedConversationId)) return;

    setDetailError("");
    setOwnershipWarning(null);
    setManagerOverrideArmed(false);
    setComposerLocked(false);
    setActiveConversation((prev) =>
      String(prev?.id || "") === String(linkedConversationId)
        ? stripConversationPermissionState(prev)
        : null
    );
    setDetailMessages([]);
    setDetailVoiceCalls([]);
    setShowConversationDetails(false);
    setActiveId(linkedConversationId);
  }, [activeId, linkedConversationId]);

  useEffect(() => {
    if (!items.length) {
      if (linkedConversationId) {
        return;
      }

      setActiveId(null);
      setActiveConversation(null);
      setDetailMessages([]);
      setDetailVoiceCalls([]);
      return;
    }

    // If the currently active conversation was removed from the list, clear it.
    if (
      activeId
      && !items.some((item) => String(item.id) === String(activeId))
      && String(linkedConversationId || "") !== String(activeId)
    ) {
      setActiveId(null);
    }
  }, [activeId, items, linkedConversationId]);

  useEffect(() => {
    if (!activeId) return undefined;

    openConversationForViewing(activeId, { showLoader: true });

    return undefined;
  }, [activeId, openConversationForViewing]);

  useEffect(() => {
    if (!activeId) return;
    setShowConversationDetails(false);
  }, [activeId]);


  // ── Adaptive polling: replaces the old brute-force 4-second setInterval ──
  // - Uses a setTimeout chain so a slow response never causes overlap.
  // - Pauses automatically while the tab is hidden; resumes on visibility.
  // - Backs off exponentially (up to 30 s) when the server returns errors.
  // - Base interval 5 s when visible (was 4 s unconditionally before).
  const ownershipClaimConflict = ownershipWarning?.claimConflict;
  useAdaptivePolling(
    useCallback(async () => {
      await syncTodayOverview();
      await syncConversationList();
      if (activeIdRef.current && !ownershipClaimConflict) {
        await syncConversationDetail(activeIdRef.current);
      }
    }, [ownershipClaimConflict, syncConversationDetail, syncConversationList, syncTodayOverview]),
    { baseInterval: 5000, maxInterval: 30000, backoffFactor: 1.8 }
  );

  useEffect(() => {
    if (!activeConversation?.id) {
      setCannedReplies([]);
      return undefined;
    }

    let cancelled = false;
    setCannedRepliesLoading(true);

    omnichannelInboxApi
      .listCannedReplies({
        channel: activeConversation?.channel,
        workstream_id: activeConversation?.workstream?.id,
      })
      .then((response) => {
        if (cancelled) return;
        setCannedReplies(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setCannedReplies([]);
      })
      .finally(() => {
        if (!cancelled) {
          setCannedRepliesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeConversation?.channel, activeConversation?.id, activeConversation?.workstream?.id]);

  // Reset composer whenever the active conversation changes.
  // Deliberately excludes pendingRestoredDraft so that consuming the draft
  // (setPendingRestoredDraft(null)) does not re-trigger this reset and wipe
  // out the text that was just restored.
  useEffect(() => {
    if (!activeConversation?.id) return;

    shouldPersistRecordingRef.current = false;
    mediaRecorderRef.current?.stop?.();
    mediaRecorderStreamRef.current?.getTracks?.().forEach((track) => track.stop());
    mediaRecorderRef.current = null;
    mediaRecorderStreamRef.current = null;
    revokeComposerAttachmentPreviews(composerAttachmentsRef.current);
    setChatComposer("");
    setComposerAttachments([]);
    setRecordingAudio(false);
    setRecordingSeconds(0);
    setEmailComposerExpanded(false);
    setMsgWindowSize(40); // reset message window on conversation switch
    setEmailComposer({
      to: activeConversation?.customer?.email || activeConversation?.contact?.email || "",
      cc: "",
      bcc: "",
      showCcBcc: false,
      subject: activeConversation?.subject ? `Re: ${activeConversation.subject}` : "Re: Your inquiry",
      body: "",
    });
  }, [
    activeConversation?.contact?.email,
    activeConversation?.customer?.email,
    activeConversation?.id,
    activeConversation?.subject,
  ]);

  // ── Restore draft from localStorage immediately after composer reset ────────
  // This runs synchronously relative to React's render cycle so the draft is
  // visible before the async syncConversationDetail call completes.
  // If the server later returns a draft (pendingRestoredDraft), that effect
  // below overrides this — and syncConversationDetail already calls
  // localStorage.removeItem() before setting pendingRestoredDraft, so by the
  // time the server-draft effect runs, localStorage is already clear and this
  // effect becomes a no-op on re-render.
  useEffect(() => {
    if (!activeConversation?.id) return;

    try {
      const localDraft = localStorage.getItem(`draft_${activeConversation.id}`);
      if (!localDraft) return;

      const channel = String(activeConversation?.channel || "").toLowerCase();
      if (channel === "email") {
        setEmailComposer((prev) => ({ ...prev, body: localDraft }));
      } else {
        setChatComposer(localDraft);
      }
      setOwnershipWarning((prev) =>
        prev?.claimConflict ? prev : { message: "Unsaved draft restored.", restoredDraft: true }
      );
    } catch (_) { /* storage unavailable */ }
  }, [activeConversation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore a saved draft into the composer after the conversation-reset effect
  // above has already cleared the fields. Runs only when pendingRestoredDraft
  // changes. When it calls setPendingRestoredDraft(null), this effect re-runs
  // but immediately returns, so there is no cascade.
  useEffect(() => {
    if (!pendingRestoredDraft || !activeConversation?.id) return;
    if (String(pendingRestoredDraft.conversationId) !== String(activeConversation.id)) return;

    if (String(pendingRestoredDraft.channel || "").toLowerCase() === "email") {
      setEmailComposer((prev) => ({
        ...prev,
        subject: pendingRestoredDraft.draft?.subject || prev.subject,
        body: pendingRestoredDraft.draft?.body || prev.body,
      }));
    } else {
      setChatComposer(pendingRestoredDraft.draft?.body || "");
    }

    setOwnershipWarning((prev) => (
      prev?.claimConflict
        ? prev
        : { message: "Saved draft restored.", restoredDraft: true }
    ));
    setPendingRestoredDraft(null);
  }, [activeConversation?.id, pendingRestoredDraft]);

  useEffect(() => () => {
    revokeComposerAttachmentPreviews(composerAttachmentsRef.current);
    mediaRecorderRef.current?.stop?.();
    mediaRecorderStreamRef.current?.getTracks?.().forEach((track) => track.stop());
  }, []);

  useEffect(() => {
    if (!recordingAudio) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setRecordingSeconds((prev) => prev + 1);
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [recordingAudio]);

  const showSnackbar = (message) => setSnackbar(message);

  // ── Draft persistence (localStorage) — extracted to useDraftPersistence ───
  const { saveDraft, clearDraft, scheduleSave: scheduleDraftSave } = useDraftPersistence();

  // ── Draft auto-save: debounced persist while typing ───────────────────────
  // Fires 2 s after the user stops typing. Covers tab-close / crash scenarios.
  // On send, clearDraft() wipes the entry so it is not wrongly restored later.
  useEffect(() => {
    const conversationId = activeConversation?.id;
    if (!conversationId) return undefined;

    const text = isEmailConversation ? emailComposer.body : chatComposer;
    if (!text.trim()) return undefined;

    return scheduleDraftSave(conversationId, text, 2000);
  }, [activeConversation?.id, chatComposer, emailComposer.body, isEmailConversation, scheduleDraftSave]);

  // ── Compose lock + heartbeat — extracted to useComposeLock ────────────────
  const {
    handleComposerFocus,
    releaseComposeLock,
    composerLockConversationIdRef,
  } = useComposeLock({
    onLockChange: setComposerLocked,
  });

  useEffect(() => {
    const previousConversationId = previousActiveConversationIdRef.current;
    if (previousConversationId && String(previousConversationId) !== String(activeId || "")) {
      releaseComposeLock(previousConversationId);
    }

    previousActiveConversationIdRef.current = activeId;
  }, [activeId, releaseComposeLock]);

  useEffect(() => () => {
    releaseComposeLock(composerLockConversationIdRef.current || activeIdRef.current);
  }, [composerLockConversationIdRef, releaseComposeLock]);

  useEffect(() => {
    if (!activeId || ownershipWarning?.claimConflict) {
      setConversationPresence({
        assigned_to: null,
        being_viewed_by: [],
        being_edited_by: null,
      });
      return undefined;
    }

    let cancelled = false;

    const syncPresence = () => {
      omnichannelInboxApi
        .getConversationPresence(activeId)
        .then((response) => {
          if (cancelled) return;
          setConversationPresence({
            assigned_to: response?.assigned_to || null,
            being_viewed_by: Array.isArray(response?.being_viewed_by) ? response.being_viewed_by : [],
            being_edited_by: response?.being_edited_by || null,
          });
        })
        .catch(() => {
          if (cancelled) return;
          setConversationPresence({
            assigned_to: null,
            being_viewed_by: [],
            being_edited_by: null,
          });
        });
    };

    syncPresence();
    const timer = window.setInterval(syncPresence, 10000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeId, ownershipWarning?.claimConflict]);

  useEffect(() => {
    const handleConversationUpdated = (event) => {
      const payload = event?.detail || {};
      const conversationId = payload?.conversation_id ? String(payload.conversation_id) : "";

      liveSyncQueueRef.current = liveSyncQueueRef.current
        .catch(() => null)
        .then(async () => {
          await syncTodayOverview();
          await syncConversationList();

          if (activeIdRef.current && (!conversationId || String(activeIdRef.current) === conversationId)) {
            await syncConversationDetail(activeIdRef.current, { showLoader: false });
          }
        })
        .catch(() => null);
    };

    window.addEventListener("omnichannel:conversation-updated", handleConversationUpdated);

    return () => {
      window.removeEventListener("omnichannel:conversation-updated", handleConversationUpdated);
    };
  }, [syncConversationDetail, syncConversationList, syncTodayOverview]);

  const openDialog = (key) => setDialogs((prev) => ({ ...prev, [key]: true }));
  const closeDialog = (key) => setDialogs((prev) => ({ ...prev, [key]: false }));
  const canReplyNormally = Boolean(activeConversation?.permissions?.can_reply);
  const canOverrideReply = Boolean(activeConversation?.permissions?.can_override_reply);
  const requiresClaimBeforeReply = Boolean(activeConversation?.permissions?.requires_claim);
  const canTakeOverConversation = Boolean(activeConversation?.permissions?.can_take_over);
  const ownershipReadOnly = Boolean(activeConversation) && !canReplyNormally && !canOverrideReply;
  const managerOverridePending = Boolean(activeConversation) && !canReplyNormally && canOverrideReply && !managerOverrideArmed;
  const presenceEditor = conversationPresence.being_edited_by || activeConversation?.lock?.locked_by || null;
  const lockedByAnotherAgent = Boolean(
    composerLocked
    || (
      canReplyNormally
      && presenceEditor?.id
      && String(presenceEditor.id) !== String(activeConversation?.assignment?.assigned_to?.id || "")
    )
  );
  const composerCommonDisabled = sendingReply
    || recordingAudio
    || (!activeConversation?.reply?.configured && !isEmailConversation)
    || isVoiceConversation
    || claimingConversation
    || ownershipReadOnly
    || lockedByAnotherAgent;
  const hasComposerAttachments = composerAttachments.length > 0;
  const canUseAudioRecording = typeof window !== "undefined"
    && typeof navigator !== "undefined"
    && !!window.MediaRecorder
    && !!navigator.mediaDevices?.getUserMedia;
  const sendButtonDisabled = composerCommonDisabled
    || managerOverridePending
    || requiresClaimBeforeReply
    || (!isEmailConversation && !chatComposer.trim() && !hasComposerAttachments)
    || (isEmailConversation && !emailComposer.body.trim() && !hasComposerAttachments);

  useEffect(() => {
    if (
      !activeConversation?.id
      || !canReplyNormally
      || requiresClaimBeforeReply
      || isVoiceConversation
      || !activeConversation?.reply?.configured
    ) {
      return;
    }

    handleComposerFocus(activeConversation.id);
  }, [
    activeConversation?.id,
    activeConversation?.reply?.configured,
    canReplyNormally,
    handleComposerFocus,
    isVoiceConversation,
    requiresClaimBeforeReply,
  ]);

  const pushComposerFiles = useCallback((files) => {
    const nextFiles = Array.from(files || []).filter(Boolean);
    if (!nextFiles.length) {
      return;
    }

    setComposerAttachments((prev) => [...prev, ...nextFiles.map((file) => makeComposerAttachment(file))]);
  }, []);

  const removeComposerAttachment = useCallback((attachmentId) => {
    setComposerAttachments((prev) => {
      const nextAttachments = [];

      prev.forEach((attachment) => {
        if (attachment.id === attachmentId) {
          revokeComposerAttachmentPreviews([attachment]);
          return;
        }

        nextAttachments.push(attachment);
      });

      return nextAttachments;
    });
  }, []);

  const handleComposerFileSelection = useCallback((event) => {
    pushComposerFiles(event?.target?.files);
    if (event?.target) {
      event.target.value = "";
    }
  }, [pushComposerFiles]);

  const handleOpenAttachmentPicker = () => {
    attachmentInputRef.current?.click();
  };

  const handleComposerPaste = useCallback((event) => {
    const items = Array.from(event?.clipboardData?.items || []);
    const imageFiles = items
      .filter((item) => item.kind === "file" && String(item.type || "").startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter(Boolean);

    if (!imageFiles.length) {
      return;
    }

    event.preventDefault();
    pushComposerFiles(imageFiles);
  }, [pushComposerFiles]);

  const handleToggleAudioRecording = useCallback(async () => {
    if (recordingAudio) {
      shouldPersistRecordingRef.current = true;
      mediaRecorderRef.current?.stop?.();
      return;
    }

    if (!canUseAudioRecording) {
      showSnackbar("Audio recording is not supported in this browser.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = recordingMimeType();
      const recorder = mimeType ? new window.MediaRecorder(stream, { mimeType }) : new window.MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      mediaRecorderStreamRef.current = stream;
      mediaRecorderChunksRef.current = [];
      shouldPersistRecordingRef.current = false;

      recorder.addEventListener("dataavailable", (event) => {
        if (event.data && event.data.size > 0) {
          mediaRecorderChunksRef.current.push(event.data);
        }
      });

      recorder.addEventListener("stop", () => {
        const chunks = [...mediaRecorderChunksRef.current];
        const finalMimeType = recorder.mimeType || mimeType || "audio/webm";

        mediaRecorderChunksRef.current = [];
        mediaRecorderRef.current = null;
        mediaRecorderStreamRef.current?.getTracks?.().forEach((track) => track.stop());
        mediaRecorderStreamRef.current = null;
        setRecordingAudio(false);
        setRecordingSeconds(0);

        if (!shouldPersistRecordingRef.current || !chunks.length) {
          shouldPersistRecordingRef.current = false;
          return;
        }

        shouldPersistRecordingRef.current = false;

        const extension = finalMimeType.includes("ogg")
          ? "ogg"
          : finalMimeType.includes("mp4") || finalMimeType.includes("aac")
            ? "m4a"
            : "webm";
        const file = new File(
          [new Blob(chunks, { type: finalMimeType })],
          `voice-note-${Date.now()}.${extension}`,
          { type: finalMimeType }
        );

        pushComposerFiles([file]);
      });

      recorder.start();
      setRecordingAudio(true);
      setRecordingSeconds(0);
    } catch (error) {
      showSnackbar(error?.message || "Unable to start audio recording.");
    }
  }, [canUseAudioRecording, pushComposerFiles, recordingAudio]);

  const handleConnectGmailMailbox = () => {
    const connectUrl = gmailMailboxStatus?.connect_url;
    if (connectUrl) {
      window.location.href = connectUrl;
    } else {
      showSnackbar("Connect your company mailbox from Settings.");
    }
  };

  const handleSyncGmailMailbox = async () => {
    setGmailMailboxSyncing(true);

    try {
      const response = await omnichannelInboxApi.syncGmailInboxNow();
      await loadGmailMailboxStatus();
      await syncConversationList();

      if (activeIdRef.current) {
        await syncConversationDetail(activeIdRef.current);
      }

      showSnackbar(response?.message || "Mailbox synced.");
    } catch (error) {
      showSnackbar(error?.response?.data?.message || error?.message || "Failed to sync the mailbox.");
    } finally {
      setGmailMailboxSyncing(false);
    }
  };

  const applyConversationPayload = (response, fallbackMessage = "Conversation updated.") => {
    const payload = unwrapDetailPayload(response);
    if (!payload?.conversation) {
      showSnackbar(fallbackMessage);
      return;
    }

    const nextMessages = sortMessagesChronologically(Array.isArray(payload.messages) ? payload.messages : []);
    const nextVoiceCalls = Array.isArray(payload.voice_calls) ? payload.voice_calls : [];

    detailSnapshotRef.current = conversationDetailSnapshot(
      payload.conversation,
      nextMessages,
      nextVoiceCalls
    );
    setActiveConversation(payload.conversation);
    setDetailMessages(nextMessages);
    setDetailVoiceCalls(nextVoiceCalls);
    setDetailError("");
    setItems((prev) => {
      const exists = prev.some((entry) => String(entry.id) === String(payload.conversation.id));
      const nextItems = sortConversationsByActivity(
        exists
          ? prev.map((entry) => (
            String(entry.id) === String(payload.conversation.id)
              ? { ...entry, ...payload.conversation }
              : entry
          ))
          : [{ ...payload.conversation }, ...prev]
      );

      listSnapshotRef.current = conversationListSnapshot(nextItems);
      return nextItems;
    });
    showSnackbar(response?.message || fallbackMessage);
  };

  const handleClaimConversation = async () => {
    if (!activeConversation?.id || claimingConversation) return;
    setClaimingConversation(true);
    try {
      await omnichannelInboxApi.claimConversation(activeConversation.id, true);
      await syncConversationDetail(activeConversation.id);
      await syncConversationList();
    } catch (error) {
      showSnackbar(error?.response?.data?.message || error?.message || "Failed to claim conversation.");
    } finally {
      setClaimingConversation(false);
    }
  };

  const handleTakeOverConversation = async () => {
    if (!activeConversation?.id || !user?.id || claimingConversation) return;

    setClaimingConversation(true);
    try {
      const response = await omnichannelInboxApi.assignConversation(activeConversation.id, user.id, false);
      applyConversationPayload(response, "Conversation handed over to you.");
      await syncConversationList();
      await syncTodayOverview();
      if (activeIdRef.current) {
        await syncConversationDetail(activeIdRef.current, { showLoader: false });
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message || error?.message || "Failed to take over conversation.");
    } finally {
      setClaimingConversation(false);
    }
  };

  const handleOpenConversationById = useCallback((conversationId, channel = "", seedConversation = null) => {
    if (!conversationId) return;

    linkedSelectionSuppressedRef.current = false;
    const resolvedConversationId = String(conversationId);
    const seededConversation = seedConversation && String(seedConversation?.id || "") === resolvedConversationId
      ? seedConversation
      : null;
    const matchingItem = seededConversation || items.find((item) => String(item.id) === resolvedConversationId) || null;
    const nextConversationSeed = stripConversationPermissionState(matchingItem);
    const resolvedChannel = channel || matchingItem?.channel || currentChannel || "";

    setDetailError("");
    setOwnershipWarning(null);
    setManagerOverrideArmed(false);
    setComposerLocked(false);
    setActiveConversation((prev) => {
      if (String(prev?.id || "") === resolvedConversationId) {
        return nextConversationSeed || stripConversationPermissionState(prev);
      }

      return nextConversationSeed;
    });
    setDetailMessages([]);
    setDetailVoiceCalls([]);
    setShowConversationDetails(false);
    syncInboxLocation(resolvedConversationId, resolvedChannel);
    setActiveId(resolvedConversationId);
  }, [currentChannel, items, syncInboxLocation]);

  const handleSelectConversation = (item) => {
    handleOpenConversationById(item?.id, item?.channel || currentChannel || "", item || null);
  };

  useEffect(() => {
    if (!pendingWelcomeAction) {
      return;
    }

    if (listLoading || listRequestRef.current < pendingWelcomeAction.requestAfter) {
      return;
    }

    const candidate = pendingWelcomeAction.mode === "hot"
      ? (visibleItems[0] || null)
      : (items[0] || null);

    if (candidate) {
      setPendingWelcomeAction(null);
      handleOpenConversationById(candidate?.id, candidate?.channel || currentChannel || "", candidate);
      return;
    }

    setPendingWelcomeAction(null);
    showSnackbar(
      pendingWelcomeAction.mode === "hot"
        ? "No hot leads match the current inbox scope."
        : "No unassigned conversations match the current inbox scope."
    );
  }, [currentChannel, handleOpenConversationById, items, listLoading, pendingWelcomeAction, visibleItems]);

  const handleSelectQuoteConversation = (conversationId) => {
    handleOpenConversationById(conversationId);
    syncConversationList();
  };

  const handleChannelDraftChange = (nextChannel) => {
    setDraftFilters((prev) => withChannelFilter(prev, nextChannel));
  };

  const handleApplyWhatsAppScope = (scope = "") => {
    const nextDraft = withWhatsAppLineScopeFilter(draftFilters, scope);
    const nextApplied = withWhatsAppLineScopeFilter(appliedFiltersRef.current, scope);

    setDraftFilters(nextDraft);
    setAppliedFilters(nextApplied);
  };

  const handleApplyConsultantLine = (lineId = "", fallbackScope = "consultant") => {
    const nextDraft = withWhatsAppLineIdFilter(draftFilters, lineId, fallbackScope);
    const nextApplied = withWhatsAppLineIdFilter(appliedFiltersRef.current, lineId, fallbackScope);

    setDraftFilters(nextDraft);
    setAppliedFilters(nextApplied);
  };

  const buildWelcomeActionFilters = useCallback((overrides = {}) => {
    const currentFilters = appliedFiltersRef.current;
    const scopedChannel = channelLocked ? initialChannel : (currentChannelRef.current || currentFilters.channel || "");
    let nextFilters = makeFilters(scopedChannel);

    if (currentFilters.group) {
      nextFilters.group = currentFilters.group;
    }

    if (String(scopedChannel || "").toLowerCase() === "whatsapp") {
      if (currentFilters.whatsappLineId) {
        nextFilters = withWhatsAppLineIdFilter(nextFilters, currentFilters.whatsappLineId, currentFilters.whatsappLineScope);
      } else if (currentFilters.whatsappLineScope) {
        nextFilters = withWhatsAppLineScopeFilter(nextFilters, currentFilters.whatsappLineScope);
      }
    }

    return {
      ...nextFilters,
      ...overrides,
    };
  }, [channelLocked, initialChannel]);

  const applyImmediateFilters = (updater) => {
    const baseFilters = appliedFiltersRef.current;
    const nextDraft = typeof updater === "function" ? updater(draftFilters) : updater;
    const nextApplied = typeof updater === "function" ? updater(baseFilters) : updater;

    setDraftFilters(nextDraft);
    setAppliedFilters(nextApplied);
  };

  const handleSelectChannelRail = (target) => {
    switch (target) {
      case "all":
        applyImmediateFilters((prev) => withChannelFilter({ ...prev, group: "" }, ""));
        break;
      case "broadcast":
        handleApplyWhatsAppScope("broadcast");
        break;
      case "rotated_work":
        handleApplyWhatsAppScope("rotated_work");
        break;
      case "consultant":
        handleApplyWhatsAppScope("consultant");
        break;
      case "support":
        if (supportWorkstream?.id) {
          applyImmediateFilters((prev) => ({ ...withChannelFilter(prev, ""), group: String(supportWorkstream.id) }));
        } else {
          showSnackbar("No customer support workstream is configured yet.");
        }
        break;
      case "email":
        applyImmediateFilters((prev) => withChannelFilter({ ...prev, group: "" }, "email"));
        break;
      default:
        break;
    }
  };

  const handleViewUnassignedConversations = () => {
    setListError("");
    setQuickListFilter("all");
    setPendingWelcomeAction({
      mode: "unassigned",
      requestAfter: listRequestRef.current + 1,
    });
    applyImmediateFilters(buildWelcomeActionFilters({ assigned: "pool" }));
  };

  const handleOpenRecentDrafts = async () => {
    const draftConversation = items.find((item) => {
      if (pendingRestoredDraft && String(pendingRestoredDraft.conversationId) === String(item?.id || "")) {
        return true;
      }

      try {
        return Boolean(localStorage.getItem(`draft_${item?.id}`));
      } catch (_error) {
        return false;
      }
    });

    if (draftConversation) {
      handleSelectConversation(draftConversation);
      return;
    }

    try {
      const response = await omnichannelInboxApi.listRecentDrafts({ limit: 1 });
      const recentDraft = Array.isArray(response?.data) ? response.data[0] : null;
      const conversation = recentDraft?.conversation || null;

      if (conversation?.id) {
        const nextFilters = withChannelFilter(makeFilters(channelLocked ? initialChannel : ""), conversation.channel || "");
        setDraftFilters(nextFilters);
        setAppliedFilters(nextFilters);

        setItems((prev) => {
          const exists = prev.some((entry) => String(entry.id) === String(conversation.id));
          const nextItems = sortConversationsByActivity(
            exists
              ? prev.map((entry) => (
                String(entry.id) === String(conversation.id)
                  ? { ...entry, ...conversation }
                  : entry
              ))
              : [conversation, ...prev]
          );

          listSnapshotRef.current = conversationListSnapshot(nextItems);
          return nextItems;
        });

        handleSelectConversation(conversation);
        return;
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message || error?.message || "Unable to load recent drafts.");
      return;
    }

    showSnackbar("No saved drafts found right now.");
  };

  const handleStartNewConversation = () => {
    const preferredChannel = ["email", "voice", "whatsapp"].includes(String(currentChannel || "").toLowerCase())
      ? String(currentChannel || "").toLowerCase()
      : "whatsapp";
    const defaultWhatsAppLineId = (() => {
      if (String(activeConversation?.channel || "").toLowerCase() === "whatsapp") {
        return activeConversation?.whatsapp_line?.id ? String(activeConversation.whatsapp_line.id) : "";
      }

      if (appliedFilters.whatsappLineId) {
        return String(appliedFilters.whatsappLineId);
      }

      if (appliedFilters.whatsappLineScope === "consultant") {
        return consultantWhatsappLines.length === 1 ? String(consultantWhatsappLines[0].id) : "";
      }

      return launchableWhatsappLines.length === 1 ? String(launchableWhatsappLines[0].id) : "";
    })();

    setNewConversationDraft(createNewConversationDraft({
      channel: preferredChannel,
      whatsappLineId: defaultWhatsAppLineId,
    }));
    setNewConversationDialogOpen(true);
  };

  const handleLaunchNewConversation = async () => {
    if (startingConversation) return;

    const channel = String(newConversationDraft.channel || "whatsapp").toLowerCase();
    const recipient = String(newConversationDraft.recipient || "").trim();
    const subject = String(newConversationDraft.subject || "").trim();
    const message = String(newConversationDraft.message || "").trim();
    const contactName = String(newConversationDraft.contactName || "").trim();
    const contactEmail = String(newConversationDraft.contactEmail || "").trim().toLowerCase();
    const saveContact = Boolean(newConversationDraft.saveContact);

    if (!recipient) {
      showSnackbar("Enter a phone number or email address first.");
      return;
    }

    if (channel === "whatsapp") {
      if (!sanitizePhoneForLink(recipient)) {
        showSnackbar("Enter a valid WhatsApp number.");
        return;
      }

      if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
        showSnackbar("Enter a valid contact email first.");
        return;
      }

      if (!newConversationDraft.whatsappLineId) {
        showSnackbar("Choose the WhatsApp line to send from.");
        return;
      }

      setStartingConversation(true);

      try {
        const response = await omnichannelInboxApi.startConversation({
          channel: "whatsapp",
          recipient,
          subject,
          body: message || undefined,
          open_without_sending: !message,
          save_contact: saveContact,
          contact_name: contactName || undefined,
          contact_email: contactEmail || undefined,
          whatsapp_line_id: Number(newConversationDraft.whatsappLineId),
        });
        const payload = unwrapDetailPayload(response);
        applyConversationPayload(response, message ? "Conversation started." : "Chat opened in inbox.");
        setNewConversationDialogOpen(false);
        if (payload?.conversation?.id) {
          setActiveId(payload.conversation.id);
          syncInboxLocation(payload.conversation.id, payload.conversation.channel || "whatsapp");
        }
      } catch (error) {
        showSnackbar(error?.response?.data?.message || error?.message || "Unable to start the conversation.");
      } finally {
        setStartingConversation(false);
      }

      return;
    } else if (channel === "email") {
      if (!message) {
        showSnackbar("Enter an opening message first.");
        return;
      }

      setStartingConversation(true);

      try {
        const response = await omnichannelInboxApi.startConversation({
          channel: "email",
          recipient,
          subject,
          body: message,
        });
        const payload = unwrapDetailPayload(response);
        applyConversationPayload(response, "Conversation started.");
        setNewConversationDialogOpen(false);
        if (payload?.conversation?.id) {
          setActiveId(payload.conversation.id);
          syncInboxLocation(payload.conversation.id, payload.conversation.channel || "email");
        }
      } catch (error) {
        showSnackbar(error?.response?.data?.message || error?.message || "Unable to start the conversation.");
      } finally {
        setStartingConversation(false);
      }

      return;
    } else {
      const phone = sanitizePhoneForLink(recipient);
      if (!phone) {
        showSnackbar("Enter a valid phone number to call.");
        return;
      }

      window.location.href = `tel:${phone}`;
    }

    setNewConversationDialogOpen(false);
    showSnackbar("Conversation launcher opened.");
  };

  const handleOpenHotLeads = () => {
    setListError("");
    setQuickListFilter("hot");
    setPendingWelcomeAction({
      mode: "hot",
      requestAfter: listRequestRef.current + 1,
    });
    applyImmediateFilters(buildWelcomeActionFilters());
  };

  const renderConversationRows = (rows, options = {}) =>
    groupByContact(rows).map((group) => (
      <ContactGroupRow
        key={group.key}
        group={group}
        activeId={activeId}
        onSelect={handleSelectConversation}
        showLineBadge={options.showLineBadge !== false}
        nowMs={activityNowMs}
      />
    ));

  const focusReplyComposer = () => {
    if (isEmailConversation) {
      setEmailComposerExpanded(true);
      setTimeout(() => emailBodyRef.current?.focus(), 120);
      return;
    }

    chatInputRef.current?.focus();
  };

  const handleCreateInquiry = async () => {
    if (!activeConversation?.id) return;

    try {
      const response = await omnichannelInboxApi.createInquiryFromConversation(activeConversation.id);
      applyConversationPayload(response, "Inquiry created.");
    } catch (error) {
      showSnackbar(error?.response?.data?.message || error?.message || "Failed to create inquiry.");
    }
  };

  const handleOpenInquiryWorkspace = () => {
    if (!activeConversation?.inquiry?.id) return;
    window.open(`/marketing?openInquiry=${activeConversation.inquiry.id}`, "_blank", "noopener,noreferrer");
  };

  const handleSendReply = async () => {
    if (!activeConversation?.id || sendingReply) return;

    const payload = isEmailConversation
      ? {
          subject: emailComposer.subject.trim(),
          body: emailComposer.body.trim(),
        }
      : {
          body: chatComposer.trim(),
        };

    if (!payload.body && !composerAttachments.length) return;

    setSendingReply(true);

    try {
      const response = await omnichannelInboxApi.sendConversationReply(activeConversation.id, {
        ...payload,
        attachments: composerAttachments.map((attachment) => attachment.file),
        manager_override: managerOverrideArmed,
      });
      applyConversationPayload(response, "Reply sent.");
      clearDraft(activeConversation.id);
      setChatComposer("");
      setEmailComposer((prev) => ({ ...prev, body: "", cc: "", bcc: "", showCcBcc: false }));
      if (isEmailConversation) {
        setEmailComposerExpanded(false);
      }
      revokeComposerAttachmentPreviews(composerAttachments);
      setComposerAttachments([]);
      setOwnershipWarning(null);
      setManagerOverrideArmed(false);
      releaseComposeLock(activeConversation.id);
    } catch (error) {
      const errData = error?.response?.data;
      // Ownership changed or compose lock stolen — auto-save draft
      if (errData?.save_draft) {
        const draftText = payload.body || "";
        if (draftText) {
          saveDraft(activeConversation.id, draftText);
        }
        setOwnershipWarning({
          message: errData.message || "Ownership changed. Your reply was saved as a draft.",
          newOwner: errData.new_owner || null,
          requiresOverride: Boolean(errData.requires_override),
        });
        if (!errData?.requires_override) {
          setManagerOverrideArmed(false);
        }
        syncConversationDetail(activeConversation.id);
      } else {
        showSnackbar(errData?.message || error?.message || "Failed to send reply.");
      }
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (nextStatus) => {
    if (!activeConversation?.id || !nextStatus || statusSaving) return;

    setStatusSaving(true);
    try {
      const response = await omnichannelInboxApi.updateConversationStatus(activeConversation.id, nextStatus);
      applyConversationPayload(response, "Status updated.");
    } catch (error) {
      showSnackbar(error?.response?.data?.message || error?.message || "Failed to update conversation status.");
    } finally {
      setStatusSaving(false);
    }
  };

  const handleInsertTemplate = (reply) => {
    if (!reply?.body) return;

    if (isEmailConversation) {
      setEmailComposer((prev) => ({
        ...prev,
        subject: prev.subject || reply.title || prev.subject,
        body: prev.body ? `${prev.body}\n${reply.body}` : reply.body,
      }));
      emailBodyRef.current?.focus();
    } else {
      setChatComposer((prev) => (prev ? `${prev}\n${reply.body}` : reply.body));
      chatInputRef.current?.focus();
    }

    setTemplateMenuAnchor(null);
  };

  const handleUseQuoteSuggestion = (suggestion) => {
    if (!suggestion) return;

    if (isEmailConversation) {
      // Expand composer first so the textarea is mounted, then fill + focus
      setEmailComposerExpanded(true);
      setEmailComposer((prev) => ({
        ...prev,
        body: prev.body ? `${prev.body}\n\n${suggestion}` : suggestion,
      }));
      // Give React one frame to mount the expanded textarea before focusing
      setTimeout(() => emailBodyRef.current?.focus(), 150);
      return;
    }

    setChatComposer((prev) => (prev ? `${prev}\n${suggestion}` : suggestion));
    // Small delay so state flush completes before focus
    setTimeout(() => chatInputRef.current?.focus(), 50);
  };

  const listPane = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        bgcolor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <Box
        sx={{
          px: "14px",
          py: "14px",
          borderBottom: "1px solid #f0f0f0",
          background: "#ffffff",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Stack direction="row" spacing={0.85} useFlexGap flexWrap="wrap" alignItems="center">
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: 15,
                fontWeight: 800,
                color: "#111111",
                fontFamily: "'Inter', system-ui, sans-serif",
              }}
            >
              {isWhatsAppMode ? "Unified Inbox" : titleLabel}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.8} sx={{ mt: 1.1 }}>
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 0.8,
              px: 1.15,
              py: 0.65,
              border: "1px solid #E5ECE6",
              borderRadius: "10px",
              bgcolor: "#FBFCFB",
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: 16, color: "#91A097" }} />
            <Box
              component="input"
              type="text"
              placeholder="Search conversations..."
              value={draftFilters.q}
              onChange={(event) => {
                const nextQuery = event.target.value;
                setDraftFilters((prev) => ({ ...prev, q: nextQuery }));
                clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = setTimeout(() => {
                  setAppliedFilters((prev) => ({ ...prev, q: nextQuery }));
                }, 350);
              }}
              sx={{
                width: "100%",
                border: "none",
                outline: "none",
                bgcolor: "transparent",
                fontSize: 12.5,
                color: "#32433A",
                fontFamily: "'Inter', system-ui, sans-serif",
                "&::placeholder": { color: "#A7B4AC" },
              }}
            />
          </Box>
          <Tooltip title={`Sort: ${sortOrder === "recent" ? "Newest first" : sortOrder === "oldest" ? "Oldest first" : sortOrder === "unread_first" ? "Unread first" : "Name A–Z"}`}>
            <Box
              component="button"
              onClick={(e) => setSortMenuAnchor(e.currentTarget)}
              sx={{
                width: 34, height: 34, borderRadius: "10px",
                border: sortOrder !== "recent" ? `1px solid ${alpha(C.whatsapp, 0.3)}` : "1px solid #D7EBD9",
                bgcolor: sortOrder !== "recent" ? alpha(C.whatsapp, 0.12) : alpha(C.whatsapp, 0.08),
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: C.whatsapp, padding: 0,
                "&:hover": { bgcolor: alpha(C.whatsapp, 0.14) },
              }}
            >
              <SwapHorizRoundedIcon sx={{ fontSize: 15 }} />
            </Box>
          </Tooltip>
          <Tooltip title="Filters">
            <Box
              component="button"
              onClick={() => setFiltersOpen(true)}
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                border: "1px solid #D7EBD9",
                bgcolor: alpha(C.whatsapp, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: C.whatsapp,
                padding: 0,
                "&:hover": { bgcolor: alpha(C.whatsapp, 0.12) },
              }}
            >
              <FilterListRoundedIcon sx={{ fontSize: 15 }} />
            </Box>
          </Tooltip>
        </Stack>

        {/* Sort menu */}
        <Menu
          anchorEl={sortMenuAnchor}
          open={Boolean(sortMenuAnchor)}
          onClose={() => setSortMenuAnchor(null)}
          PaperProps={{ sx: { borderRadius: "12px", minWidth: 170, boxShadow: "0 8px 24px -8px rgba(0,0,0,0.14)" } }}
        >
          {[
            { value: "recent",      label: "Newest first" },
            { value: "oldest",      label: "Oldest first" },
            { value: "unread_first",label: "Unread first" },
            { value: "name_az",     label: "Name A–Z" },
          ].map((opt) => (
            <MenuItem
              key={opt.value}
              selected={sortOrder === opt.value}
              onClick={() => { setSortOrder(opt.value); setSortMenuAnchor(null); }}
              sx={{ fontSize: 13, fontWeight: sortOrder === opt.value ? 700 : 400,
                color: sortOrder === opt.value ? C.whatsapp : C.text }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Menu>

        <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
          <InboxQuickFilterButton
            label="All"
            active={quickListFilter === "all"}
            onClick={() => setQuickListFilter("all")}
            accent={C.whatsapp}
          />
          <InboxQuickFilterButton
            label="Unread"
            count={unreadConversationsCount}
            active={quickListFilter === "unread"}
            onClick={() => setQuickListFilter("unread")}
            accent="#0F766E"
          />
          <InboxQuickFilterButton
            label="HOT"
            count={hotLeadCount}
            active={quickListFilter === "hot"}
            onClick={() => setQuickListFilter("hot")}
            accent="#DC2626"
          />
          <InboxQuickFilterButton
            label="Quotes"
            count={trackedQuotesCount}
            active={false}
            onClick={() => setQuoteQueueOpen(true)}
            accent="#C17A08"
          />
        </Stack>

        {isWhatsAppMode ? (
          <Stack spacing={0.8} sx={{ mt: 0.95 }}>
            {filterableWhatsappLines.length ? (
              <TextField
                select
                size="small"
                value={
                  draftFilters.whatsappLineId
                    || (draftFilters.whatsappLineScope === "consultant"
                      ? "__consultant__"
                      : draftFilters.whatsappLineScope === "rotated_work"
                        ? "__rotated_work__"
                        : draftFilters.whatsappLineScope === "broadcast"
                          ? "__broadcast__"
                        : "__all__")
                }
                onChange={(event) => {
                  const nextValue = event.target.value;

                  if (nextValue === "__all__") {
                    handleApplyWhatsAppScope("");
                    return;
                  }

                  if (nextValue === "__broadcast__") {
                    handleApplyWhatsAppScope("broadcast");
                    return;
                  }

                  if (nextValue === "__rotated_work__") {
                    handleApplyWhatsAppScope("rotated_work");
                    return;
                  }

                  if (nextValue === "__consultant__") {
                    handleApplyConsultantLine("", "consultant");
                    return;
                  }

                  handleApplyConsultantLine(nextValue, "consultant");
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 34,
                    borderRadius: "10px",
                    bgcolor: "#FFFFFF",
                  },
                  "& .MuiSelect-select": {
                    py: 0.8,
                    fontSize: 12,
                  },
                }}
              >
                <MenuItem value="__all__">All WhatsApp lines</MenuItem>
                <MenuItem value="__broadcast__">Broadcast number</MenuItem>
                <MenuItem value="__rotated_work__">Rotated work line</MenuItem>
                <MenuItem value="__consultant__">All direct lines</MenuItem>
                {filterableWhatsappLines.map((line) => (
                  <MenuItem key={line.id} value={String(line.id)}>
                    {line.display_name} • {whatsappLineTypeLabel(line.line_type)}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
          </Stack>
        ) : null}

        {showGmailMailboxCard ? (
          <Paper
            elevation={0}
            sx={{
              mt: 1.2,
              borderRadius: 2,
              border: `1px solid ${alpha(C.text, 0.07)}`,
              bgcolor: "#FFFFFF",
              px: 1.2,
              py: 1.1,
            }}
          >
            <Stack spacing={0.9}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ color: C.text, fontWeight: 800 }}>
                    Company mailbox
                  </Typography>
                  <Typography variant="caption" sx={{ color: gmailMailboxStatus?.has_error ? C.error || "#b91c1c" : C.subtext, display: "block", mt: 0.15 }}>
                    {gmailMailboxStatus?.connected
                      ? `${gmailMailboxStatus?.google_email || gmailMailboxStatus?.monitored_mailbox || "Mailbox"} — connected for inbound email.`
                      : gmailMailboxStatus?.has_error
                      ? `${gmailMailboxStatus?.google_email || gmailMailboxStatus?.monitored_mailbox || "Mailbox"} — token expired, reconnect to resume sync.`
                      : `${gmailMailboxStatus?.monitored_mailbox || "Company mailbox"} not connected yet.`}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                  {!gmailMailboxStatus?.connected ? (
                    <Button
                      variant="contained"
                      color={gmailMailboxStatus?.has_error ? "error" : "primary"}
                      onClick={handleConnectGmailMailbox}
                      disabled={gmailMailboxLoading}
                      sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, boxShadow: "none" }}
                    >
                      {gmailMailboxStatus?.has_error ? "Reconnect" : "Connect"}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={handleSyncGmailMailbox}
                      disabled={gmailMailboxSyncing}
                      sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
                    >
                      {gmailMailboxSyncing ? "Syncing..." : "Sync now"}
                    </Button>
                  )}
                </Stack>
              </Stack>

              {gmailMailboxStatus?.gmail_last_synced_at ? (
                <Typography variant="caption" sx={{ color: C.subtext }}>
                  Last sync {formatDetailTimestamp(gmailMailboxStatus.gmail_last_synced_at)}
                </Typography>
              ) : null}
            </Stack>
          </Paper>
        ) : null}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: "auto",
          background: "#ffffff",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {listError ? <Alert severity="error" sx={{ m: 1.25 }}>{listError}</Alert> : null}

        {listLoading ? (
          <Box>
            {[1, 2, 3, 4, 5, 6, 7].map((slot) => (
              <Box
                key={slot}
                sx={{
                  px: "12px", py: "11px",
                  borderBottom: "1px solid #f9f9f9",
                  display: "flex", gap: "10px", alignItems: "flex-start",
                }}
              >
                {/* Avatar skeleton */}
                <Box className="ibx-skeleton" sx={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0, mt: "1px" }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Name + time row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: "7px" }}>
                    <Box className="ibx-skeleton" sx={{ height: 11, borderRadius: "6px", width: `${45 + (slot % 3) * 12}%` }} />
                    <Box className="ibx-skeleton" sx={{ height: 9, borderRadius: "6px", width: 28, ml: 1 }} />
                  </Box>
                  {/* Preview line */}
                  <Box className="ibx-skeleton" sx={{ height: 9, borderRadius: "6px", width: `${60 + (slot % 4) * 8}%`, mb: "6px" }} />
                  {/* Badges row */}
                  <Box sx={{ display: "flex", gap: "5px" }}>
                    <Box className="ibx-skeleton" sx={{ height: 14, borderRadius: "20px", width: 42 }} />
                    {slot % 2 === 0 && <Box className="ibx-skeleton" sx={{ height: 14, borderRadius: "20px", width: 34 }} />}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : visibleItems.length ? (
          showWhatsAppLineSections ? (
            <Box>
              {broadcastLineItems.length ? (
                <Box>
                  <ConversationSectionLabel
                    label="Broadcast Number"
                    helper={`${broadcastLineItems.length} thread${broadcastLineItems.length === 1 ? "" : "s"}`}
                    color="#A76400"
                    tint="#FFF9ED"
                  />
                  {renderConversationRows(broadcastLineItems, { showLineBadge: false })}
                </Box>
              ) : null}

              {rotatedWorkLineItems.length ? (
                <Box>
                  <ConversationSectionLabel
                    label="Rotated Work Line"
                    helper={`${rotatedWorkLineItems.length} thread${rotatedWorkLineItems.length === 1 ? "" : "s"}`}
                    color="#0F766E"
                    tint="#F2FCF8"
                  />
                  {renderConversationRows(rotatedWorkLineItems, { showLineBadge: false })}
                </Box>
              ) : null}

              {consultantLineItems.length ? (
                <Box>
                  <ConversationSectionLabel
                    label="Direct Lines"
                    helper={`${consultantLineItems.length} thread${consultantLineItems.length === 1 ? "" : "s"}`}
                    color="#245BCE"
                    tint="#F3F7FF"
                  />
                  {renderConversationRows(consultantLineItems, { showLineBadge: false })}
                </Box>
              ) : null}
            </Box>
          ) : (
            renderConversationRows(visibleItems)
          )
        ) : (
          <ConversationEmptyState
            title="Nothing here yet"
            description={
              quickListFilter === "all"
                ? "No conversations match the current search or filters."
                : "No conversations match the current quick filter."
            }
            eyebrow="Conversation List"
            accentColor={C.whatsapp}
          />
        )}
      </Box>
    </Box>
  );

  const conversationHeader = activeConversation ? (
    <Box
      sx={{
        borderBottom: `1px solid ${alpha(C.text, 0.07)}`,
        background: `linear-gradient(180deg, ${alpha(activeMeta.color, 0.03)} 0%, #FFFFFF 60%)`,
        flexShrink: 0,
        fontFamily: "'Inter', system-ui, sans-serif",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${activeMeta.color} 0%, ${alpha(activeMeta.color, 0.25)} 100%)`,
          borderRadius: "0 0 3px 3px",
        },
      }}
    >
      {/* ── Primary row ── */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{ height: 64, px: { xs: "12px", md: "16px" }, gap: "10px", pt: "3px" }}
      >
        {/* Back */}
        <Tooltip title="Back to conversations">
          <IconButton
            onClick={handleReturnToConversationList}
            size="small"
            sx={{
              width: 32, height: 32, flexShrink: 0,
              color: C.subtext, borderRadius: "10px",
              border: `1px solid ${alpha(C.text, 0.09)}`,
              bgcolor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              "&:hover": { bgcolor: alpha(activeMeta.color, 0.07), color: activeMeta.color, borderColor: alpha(activeMeta.color, 0.25) },
              transition: "all 150ms ease",
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>

        {/* Avatar with channel dot */}
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Avatar
            sx={{
              width: 40, height: 40,
              bgcolor: alpha(activeMeta.color, 0.11),
              color: activeMeta.color,
              fontWeight: 800, fontSize: 14,
              borderRadius: "12px",
              fontFamily: "'Inter', system-ui, sans-serif",
              border: `2px solid ${alpha(activeMeta.color, 0.18)}`,
              boxShadow: `0 2px 8px -2px ${alpha(activeMeta.color, 0.25)}`,
            }}
          >
            {initials(activeConversation?.customer?.name || activeConversation?.contact?.name || "C")}
          </Avatar>
          <Box sx={{
            position: "absolute", bottom: -2, right: -2,
            width: 16, height: 16, borderRadius: "50%",
            bgcolor: activeMeta.color,
            border: "2.5px solid #FFFFFF",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 1px 4px ${alpha(activeMeta.color, 0.4)}`,
            "& svg": { fontSize: 8, color: "#FFFFFF" },
          }}>
            {activeMeta.icon}
          </Box>
        </Box>

        {/* Name + sub-line */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.7} useFlexGap sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 15, fontWeight: 700, color: C.text,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                lineHeight: 1.2, letterSpacing: "-0.15px",
              }}
            >
              {activeConversation?.customer?.name || activeConversation?.contact?.name || "Conversation"}
            </Typography>
            {activeConversation?.inquiry?.status ? (
              <Box sx={{
                px: "8px", py: "2px", borderRadius: "6px",
                bgcolor: alpha(C.whatsapp, 0.09),
                border: `1px solid ${alpha(C.whatsapp, 0.2)}`,
              }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.whatsapp, letterSpacing: "0.3px", textTransform: "uppercase", lineHeight: 1.4 }}>
                  {activeConversation.inquiry.status}
                </Typography>
              </Box>
            ) : null}
            {activeConversation?.inquiry?.temperature ? (
              <Box sx={{
                px: "8px", py: "2px", borderRadius: "6px",
                bgcolor: alpha("#DC2626", 0.07),
                border: `1px solid ${alpha("#DC2626", 0.18)}`,
              }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#DC2626", letterSpacing: "0.3px", textTransform: "uppercase", lineHeight: 1.4 }}>
                  {activeConversation.inquiry.temperature}
                </Typography>
              </Box>
            ) : null}
          </Stack>

          {/* Sub-line: address + activity */}
          <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap sx={{ mt: "3px", minWidth: 0, flexWrap: "nowrap", overflow: "hidden" }}>
            {activeCustomerAddress ? (
              <Typography variant="caption" sx={{ color: C.subtext, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200, fontSize: 12 }}>
                {activeCustomerAddress}
              </Typography>
            ) : null}
            {activeCustomerAddress && customerActivityStatus ? (
              <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: alpha(C.text, 0.25), flexShrink: 0 }} />
            ) : null}
            {customerActivityStatus ? (
              <Stack direction="row" spacing={0.45} alignItems="center" sx={{ flexShrink: 0 }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                  bgcolor: customerActivityStatus.tone === "success" ? C.mint : customerActivityStatus.tone === "neutral" ? C.shellMuted : alpha(C.text, 0.3),
                  boxShadow: customerActivityStatus.tone === "success" ? `0 0 0 2px ${alpha(C.mint, 0.2)}` : "none",
                }} />
                <Typography variant="caption" sx={{ color: C.subtext, fontWeight: 500, whiteSpace: "nowrap", fontSize: 12 }}>
                  {customerActivityStatus.label}{customerActivityStatus.helper ? ` · ${customerActivityStatus.helper}` : ""}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </Box>

        {/* Right side: status select + divider + actions */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          {/* Status select */}
          <TextField
            select
            size="small"
            value={activeConversation?.status || "open"}
            onChange={(event) => handleUpdateStatus(event.target.value)}
            disabled={statusSaving}
            sx={{
              flexShrink: 0,
              minWidth: 110,
              "& .MuiOutlinedInput-root": {
                height: 32, borderRadius: "10px",
                bgcolor: "#FFFFFF",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                "& fieldset": { borderColor: alpha(C.text, 0.1) },
                "&:hover fieldset": { borderColor: alpha(activeMeta.color, 0.35) },
              },
              "& .MuiSelect-select": { py: 0.5, fontSize: 12, fontWeight: 700, color: C.text, pr: "28px !important" },
            }}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          {/* Thin divider */}
          <Box sx={{ width: "1px", height: 22, bgcolor: alpha(C.text, 0.1), flexShrink: 0 }} />

          {/* Action group in a subtle pill */}
          <Stack
            direction="row"
            spacing={0}
            sx={{
              flexShrink: 0,
              bgcolor: alpha(C.text, 0.025),
              border: `1px solid ${alpha(C.text, 0.07)}`,
              borderRadius: "12px",
              p: "3px",
              gap: "1px",
            }}
          >
            <HeaderActionIcon title="Reply" onClick={focusReplyComposer} disabled={!activeConversation?.reply?.configured && !isEmailConversation} color={activeMeta.color}>
              <SendRoundedIcon sx={{ fontSize: 16 }} />
            </HeaderActionIcon>
            <HeaderActionIcon title="Note" onClick={() => openDialog("note")}>
              <NotesRoundedIcon sx={{ fontSize: 16 }} />
            </HeaderActionIcon>
            <HeaderActionIcon title="Handover" onClick={() => openDialog("assign")}>
              <SwapHorizRoundedIcon sx={{ fontSize: 16 }} />
            </HeaderActionIcon>
            <HeaderActionIcon title="Link Inquiry" onClick={() => openDialog("linkInquiry")}>
              <LinkRoundedIcon sx={{ fontSize: 16 }} />
            </HeaderActionIcon>
            <HeaderActionIcon title="Follow-up" onClick={() => openDialog("followUp")}>
              <TextSnippetRoundedIcon sx={{ fontSize: 15 }} />
            </HeaderActionIcon>
            {isWhatsAppConversation ? (
              <HeaderActionIcon title="Call via Phone Link" onClick={() => {
                const phone = activeConversation?.customer?.phone || activeConversation?.contact?.phone || "";
                if (phone) window.location.href = `tel:${phone}`;
              }}>
                <CallOutlinedIcon sx={{ fontSize: 16 }} />
              </HeaderActionIcon>
            ) : isEmailConversation ? (
              <HeaderActionIcon title="Schedule Google Meet" onClick={() => window.open("https://meet.google.com/new", "_blank", "noopener")}>
                <VideoCallOutlinedIcon sx={{ fontSize: 16 }} />
              </HeaderActionIcon>
            ) : (
              <HeaderActionIcon title="Start Call" onClick={() => openDialog("liveCall")}>
                <CallOutlinedIcon sx={{ fontSize: 16 }} />
              </HeaderActionIcon>
            )}
            <HeaderActionIcon title="Log Call" onClick={() => openDialog("voiceLog")}>
              <AssignmentIndRoundedIcon sx={{ fontSize: 16 }} />
            </HeaderActionIcon>
            {showConversationDetailsToggle ? (
              <Tooltip title={showConversationDetails ? "Hide details panel" : "Show details panel"}>
                <Box
                  component="button"
                  onClick={() => setShowConversationDetails((prev) => !prev)}
                  sx={{
                    width: 30, height: 30, borderRadius: "8px",
                    border: showConversationDetails ? `1px solid ${alpha(activeMeta.color, 0.22)}` : "1px solid transparent",
                    bgcolor: showConversationDetails ? alpha(activeMeta.color, 0.08) : "transparent",
                    color: showConversationDetails ? activeMeta.color : C.subtext,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", padding: 0, flexShrink: 0,
                    "&:hover": { bgcolor: showConversationDetails ? alpha(activeMeta.color, 0.13) : alpha(C.text, 0.05) },
                    transition: "all 140ms ease",
                  }}
                >
                  {showConversationDetails
                    ? <ExpandLessRoundedIcon sx={{ fontSize: 15 }} />
                    : <ExpandMoreRoundedIcon sx={{ fontSize: 15 }} />}
                </Box>
              </Tooltip>
            ) : null}
            <HeaderActionIcon title="More" onClick={(event) => setHeaderMenuAnchor(event.currentTarget)}>
              <MoreVertRoundedIcon sx={{ fontSize: 16 }} />
            </HeaderActionIcon>
          </Stack>
        </Stack>
      </Stack>

      {/* ── Secondary info row (chips) ── */}
      {(activeWhatsAppLine || headerJourneyChip || headerQuoteBehaviorChip || headerNextBestActionChip || headerOwnershipChip || headerOwnershipContextChips.length > 0 || headerCustomerChips.length > 0 || (isEmailConversation && activeConversationSubject)) ? (
        <Stack
          direction="row"
          spacing={0.6}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          sx={{
            px: { xs: "12px", md: "16px" },
            pb: "10px",
            pt: "2px",
            borderTop: `1px solid ${alpha(C.text, 0.05)}`,
            background: alpha(C.text, 0.012),
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          {isEmailConversation && activeConversationSubject ? (
            <Typography variant="caption" sx={{ color: C.subtext, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%", fontSize: 12 }}>
              {activeConversationSubject}
            </Typography>
          ) : null}
          <WhatsAppLineIdentityPanel line={activeWhatsAppLine} coverageCount={activeWhatsAppCoverageCount} />
          {[headerOwnershipChip, ...headerOwnershipContextChips, headerJourneyChip, headerQuoteBehaviorChip, headerNextBestActionChip, ...headerCustomerChips].filter(Boolean).map((chip) => (
            <Chip
              key={chip.key}
              size="small"
              label={chip.label}
              sx={{
                height: 22, borderRadius: "8px", fontWeight: 700, fontSize: 10,
                letterSpacing: "0.2px",
                bgcolor:
                  chip.tone === "success" ? alpha(C.mint, 0.08) :
                  chip.tone === "info"    ? alpha(C.sapphire, 0.07) :
                  chip.tone === "danger"  ? alpha("#F07A72", 0.08) :
                  chip.tone === "warning" ? alpha(C.shellMuted, 0.06) : alpha(C.text, 0.03),
                color:
                  chip.tone === "danger"  ? "#C65050" :
                  chip.tone === "success" ? "#1a7a2e" :
                  chip.tone === "info"    ? C.text :
                  chip.tone === "warning" ? C.shellMuted : C.subtext,
                border:
                  chip.tone === "info"    ? `1px solid ${alpha(C.whatsapp, 0.2)}` :
                  chip.tone === "neutral" ? `1px solid ${alpha(C.shellMuted, 0.14)}` :
                  `1px solid ${alpha(C.text, 0.07)}`,
              }}
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  ) : null;

  const composerFooter = activeConversation ? (
    <Box
      sx={{
        mx: isWhatsAppConversation ? 0 : { xs: 1.05, md: 1.3 },
        mb: isWhatsAppConversation ? 0 : 1.1,
        mt: isWhatsAppConversation ? 0 : 0.4,
        p: isWhatsAppConversation ? { xs: 0.55, md: 0.7 } : 0,
        borderRadius: isWhatsAppConversation ? 0 : 3,
        borderTop: isWhatsAppConversation ? `1px solid ${alpha(C.text, 0.08)}` : "none",
        background: "transparent",
        boxShadow: "none",
        flexShrink: 0,
      }}
    >
      {/* ── Ownership / lock warning banner ── */}
      {ownershipWarning ? (
        <Alert
          severity="warning"
          onClose={() => setOwnershipWarning(null)}
          action={canOverrideReply && !canReplyNormally && !managerOverrideArmed ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => setManagerOverrideArmed(true)}
              sx={{ fontWeight: 700, textTransform: "none" }}
            >
              Enable override
            </Button>
          ) : null}
          sx={{ mb: 0.8, borderRadius: 2, fontSize: 13 }}
        >
          {ownershipWarning.message}
        </Alert>
      ) : composerLocked ? (
        <Alert severity="info" sx={{ mb: 0.8, borderRadius: 2, fontSize: 13 }}>
          {presenceEditor?.name
            ? `${presenceEditor.name} is actively composing right now.`
            : "Another agent is currently composing a reply."}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={0.6} sx={{ mb: 0.8, flexWrap: "wrap" }}>
        {requiresClaimBeforeReply ? (
          <Chip
            size="small"
            color="info"
            label={claimingConversation ? "Claiming…" : "Claim to reply"}
            onClick={handleClaimConversation}
            disabled={claimingConversation}
            sx={{ borderRadius: "999px", fontWeight: 700, cursor: "pointer" }}
          />
        ) : null}
        {!requiresClaimBeforeReply && canTakeOverConversation ? (
          <Chip
            size="small"
            color="warning"
            label={claimingConversation ? "Taking over…" : "Take over chat"}
            onClick={handleTakeOverConversation}
            disabled={claimingConversation}
            sx={{ borderRadius: "999px", fontWeight: 700, cursor: "pointer" }}
          />
        ) : null}
        {managerOverridePending ? (
          <Chip
            size="small"
            color="warning"
            label="Manager override required"
            onClick={() => setManagerOverrideArmed(true)}
            sx={{ borderRadius: "999px", fontWeight: 700 }}
          />
        ) : null}
        {managerOverrideArmed ? (
          <Chip
            size="small"
            color="success"
            label="Override armed"
            onDelete={() => setManagerOverrideArmed(false)}
            sx={{ borderRadius: "999px", fontWeight: 700 }}
          />
        ) : null}
      </Stack>

      {isEmailConversation ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: emailComposerExpanded ? "14px 14px 12px 12px" : "999px",
            border: `1px solid ${alpha(C.shellMuted, emailComposerExpanded ? 0.16 : 0.1)}`,
            overflow: "hidden",
            boxShadow: emailComposerExpanded
              ? "0 -4px 24px -8px rgba(15,23,42,0.10)"
              : "0 2px 8px -4px rgba(15,23,42,0.06)",
            bgcolor: "#FFFFFF",
            transition: "border-radius 200ms ease, box-shadow 200ms ease",
          }}
        >
          {/* ── Collapsed prompt bar ── */}
          {!emailComposerExpanded ? (
            <Box
              onClick={() => setEmailComposerExpanded(true)}
              sx={{
                px: 2, py: 1.1,
                display: "flex", alignItems: "center", gap: 1.2,
                cursor: "text",
                "&:hover": { bgcolor: alpha(C.text, 0.02) },
              }}
            >
              <EmailProviderIcon
                provider={resolveConversationEmailProvider(activeConversation)}
                size={16}
                sx={{ flexShrink: 0 }}
              />
              <Typography sx={{ fontSize: 13, color: "#aaaaaa", flexGrow: 1, userSelect: "none" }}>
                Reply to {activeConversation?.customer?.name || activeConversation?.customer?.email || "customer"}…
              </Typography>
              <Tooltip title="Expand reply">
                <IconButton size="small" sx={{ color: C.subtext }}>
                  <ExpandLessRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <>
              {/* ── Expanded header ── */}
              <Box sx={{
                px: 1.5, py: 0.85,
                borderBottom: `1px solid ${alpha(C.shellMuted, 0.10)}`,
                bgcolor: "#FAFAFA",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <Typography variant="body2" sx={{ color: C.text, fontWeight: 700, fontSize: 13 }}>
                  Reply
                </Typography>
                <Tooltip title="Collapse">
                  <IconButton
                    size="small"
                    onClick={() => setEmailComposerExpanded(false)}
                    sx={{ color: C.subtext }}
                  >
                    <ExpandMoreRoundedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* ── Fields ── */}
              <Box sx={{ px: 1.5, py: 0.9, bgcolor: "#FFFFFF" }}>
                <Stack spacing={0.65}>
                  <TextField
                    variant="standard"
                    fullWidth
                    label="To"
                    value={emailComposer.to}
                    onChange={(event) => setEmailComposer((prev) => ({ ...prev, to: event.target.value }))}
                  />
                  {emailComposer.showCcBcc ? (
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label="Cc"
                        value={emailComposer.cc}
                        onChange={(event) => setEmailComposer((prev) => ({ ...prev, cc: event.target.value }))}
                      />
                      <TextField
                        variant="standard"
                        fullWidth
                        label="Bcc"
                        value={emailComposer.bcc}
                        onChange={(event) => setEmailComposer((prev) => ({ ...prev, bcc: event.target.value }))}
                      />
                    </Stack>
                  ) : (
                    <Button
                      variant="text"
                      sx={{ alignSelf: "flex-start", textTransform: "none", px: 0, minWidth: 0, fontWeight: 700 }}
                      onClick={() => setEmailComposer((prev) => ({ ...prev, showCcBcc: true }))}
                    >
                      Add Cc / Bcc
                    </Button>
                  )}
                  <TextField
                    variant="standard"
                    fullWidth
                    label="Subject"
                    value={emailComposer.subject}
                    disabled={ownershipReadOnly || claimingConversation}
                    onChange={(event) => setEmailComposer((prev) => ({ ...prev, subject: event.target.value }))}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={14}
                    inputRef={emailBodyRef}
                    placeholder="Write your reply…"
                    value={emailComposer.body}
                    disabled={ownershipReadOnly || claimingConversation}
                    onChange={(event) => setEmailComposer((prev) => ({ ...prev, body: event.target.value }))}
                    onFocus={() => handleComposerFocus(activeConversation?.id)}
                    onPaste={handleComposerPaste}
                    sx={{
                      mt: 0.5,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        bgcolor: "#FAFAFA",
                        alignItems: "flex-start",
                        fontSize: 13,
                      },
                    }}
                  />
                  {recordingAudio ? (
                    <Typography variant="caption" sx={{ color: C.subtext, fontWeight: 700 }}>
                      Recording audio… {recordingSeconds}s
                    </Typography>
                  ) : null}
                  <ComposerAttachmentStrip attachments={composerAttachments} onRemove={removeComposerAttachment} />
                </Stack>
              </Box>

              <Divider />

              <Box sx={{ px: 1.2, py: 0.85, bgcolor: "#FAFAFA" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Stack direction="row" spacing={0.15} alignItems="center">
                    <HeaderActionIcon title="Bold">
                      <FormatBoldRoundedIcon sx={{ fontSize: 18 }} />
                    </HeaderActionIcon>
                    <HeaderActionIcon title="Italic">
                      <FormatItalicRoundedIcon sx={{ fontSize: 18 }} />
                    </HeaderActionIcon>
                    <HeaderActionIcon title="Underline">
                      <FormatUnderlinedRoundedIcon sx={{ fontSize: 18 }} />
                    </HeaderActionIcon>
                    <HeaderActionIcon title="Bulleted list">
                      <FormatListBulletedRoundedIcon sx={{ fontSize: 18 }} />
                    </HeaderActionIcon>
                    <HeaderActionIcon title="Templates" onClick={(event) => setTemplateMenuAnchor(event.currentTarget)}>
                      <TextSnippetRoundedIcon sx={{ fontSize: 18 }} />
                    </HeaderActionIcon>
                    <HeaderActionIcon title="Attach files" onClick={handleOpenAttachmentPicker}>
                      <AttachFileRoundedIcon sx={{ fontSize: 18 }} />
                    </HeaderActionIcon>
                    <HeaderActionIcon title={recordingAudio ? "Stop recording" : "Record audio"} onClick={handleToggleAudioRecording} disabled={!canUseAudioRecording}>
                      {recordingAudio ? <StopRoundedIcon sx={{ fontSize: 18 }} /> : <MicRoundedIcon sx={{ fontSize: 18 }} />}
                    </HeaderActionIcon>
                  </Stack>

                  <Button
                    variant="contained"
                    onClick={handleSendReply}
                    disabled={sendButtonDisabled}
                    sx={{ borderRadius: "10px", px: 2.2, textTransform: "none", fontWeight: 700, boxShadow: "none" }}
                  >
                    {sendingReply ? "Sending…" : "Send"}
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      ) : isVoiceConversation ? (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: `1px solid ${alpha(C.shellMuted, 0.12)}`, p: 1.4, bgcolor: C.surfaceStrong }}>
          <Typography variant="body2" sx={{ color: C.subtext }}>
            Voice conversations do not have a live reply composer. Use the header actions for notes, handover, follow-up, and call logging.
          </Typography>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: isWhatsAppConversation ? 0 : 3,
            border: isWhatsAppConversation ? "none" : `1px solid ${alpha(C.shellMuted, 0.12)}`,
            px: isWhatsAppConversation ? 0.35 : 1.1,
            py: isWhatsAppConversation ? 0.2 : 0.85,
            bgcolor: isWhatsAppConversation ? "transparent" : C.surfaceStrong,
            boxShadow: "none",
          }}
        >
          {recordingAudio ? (
            <Typography variant="caption" sx={{ display: "block", px: 0.6, pb: 0.6, color: C.subtext, fontWeight: 700 }}>
              Recording audio... {recordingSeconds}s
            </Typography>
          ) : null}
          <ComposerAttachmentStrip attachments={composerAttachments} onRemove={removeComposerAttachment} />
          <Stack direction="row" spacing={0.45} alignItems="center">
            <HeaderActionIcon title="Templates" onClick={(event) => setTemplateMenuAnchor(event.currentTarget)}>
              <TextSnippetRoundedIcon sx={{ fontSize: 18 }} />
            </HeaderActionIcon>
            <HeaderActionIcon title="Attach files" onClick={handleOpenAttachmentPicker}>
              <AttachFileRoundedIcon sx={{ fontSize: 18 }} />
            </HeaderActionIcon>
            <HeaderActionIcon title={recordingAudio ? "Stop recording" : "Record audio"} onClick={handleToggleAudioRecording} disabled={!canUseAudioRecording}>
              {recordingAudio ? <StopRoundedIcon sx={{ fontSize: 18 }} /> : <MicRoundedIcon sx={{ fontSize: 18 }} />}
            </HeaderActionIcon>
            <HeaderActionIcon title="Quick actions" disabled>
              <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
            </HeaderActionIcon>
            <TextField
              fullWidth
              multiline
              maxRows={5}
              inputRef={chatInputRef}
              placeholder={activeConversation?.reply?.configured ? "Type a message" : "Reply sending is not configured for this channel"}
              value={chatComposer}
              onChange={(event) => setChatComposer(event.target.value)}
              onFocus={() => handleComposerFocus(activeConversation?.id)}
              onPaste={handleComposerPaste}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  if (!sendButtonDisabled && chatComposer.trim()) {
                    handleSendReply();
                  }
                }
              }}
              disabled={composerCommonDisabled}
              variant="standard"
              InputProps={{ disableUnderline: true }}
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: 15,
                  borderRadius: isWhatsAppConversation ? "999px" : 0,
                  bgcolor: isWhatsAppConversation ? "#FFFFFF" : "transparent",
                  px: isWhatsAppConversation ? 1.7 : 0,
                  py: isWhatsAppConversation ? 0.7 : 0,
                },
              }}
            />
            <IconButton
              onClick={handleSendReply}
              disabled={sendButtonDisabled}
              sx={{
                width: 42,
                height: 42,
                borderRadius: "10px",
                bgcolor: sendButtonDisabled ? "#EEF2F0" : C.whatsapp,
                color: sendButtonDisabled ? "#92A196" : "#FFFFFF",
                "&:hover": {
                  bgcolor: sendButtonDisabled ? "#EEF2F0" : "#23992F",
                },
              }}
            >
              {sendingReply ? <CircularProgress size={18} sx={{ color: "#FFFFFF" }} /> : <SendRoundedIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Stack>
        </Paper>
      )}
    </Box>
  ) : null;

  return (
    <>
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(180deg, #FCFEFC 0%, #F4F9F4 100%)",
        }}
      >
        <Box
          sx={{
            px: { xs: 1.2, md: 1.8 },
            py: 1,
            borderBottom: `1px solid ${alpha(C.text, 0.08)}`,
            background: "#FFFFFF",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={0.85} useFlexGap flexWrap="wrap">
              <InboxChannelRailButton
                label="All Channels"
                count={items.length}
                active={!currentChannel && !appliedFilters.whatsappLineScope && !appliedFilters.whatsappLineId}
                onClick={() => handleSelectChannelRail("all")}
                icon={<AllInboxRoundedIcon sx={{ fontSize: 17 }} />}
                accent={C.whatsapp}
              />
              <InboxChannelRailButton
                label="Broadcast"
                count={broadcastWhatsappThreadCount}
                active={currentChannel === "whatsapp" && appliedFilters.whatsappLineScope === "broadcast"}
                onClick={() => handleSelectChannelRail("broadcast")}
                icon={<WhatsAppIcon sx={{ fontSize: 17 }} />}
                accent="#C17A08"
              />
              <InboxChannelRailButton
                label="Rotated Work"
                count={rotatedWorkWhatsappThreadCount}
                active={currentChannel === "whatsapp" && appliedFilters.whatsappLineScope === "rotated_work"}
                onClick={() => handleSelectChannelRail("rotated_work")}
                icon={<WhatsAppIcon sx={{ fontSize: 17 }} />}
                accent="#0F766E"
              />
              <InboxChannelRailButton
                label="Direct Lines"
                count={consultantWhatsappThreadCount}
                active={currentChannel === "whatsapp" && appliedFilters.whatsappLineScope === "consultant" && !appliedFilters.whatsappLineId}
                onClick={() => handleSelectChannelRail("consultant")}
                icon={<WhatsAppIcon sx={{ fontSize: 17 }} />}
                accent="#245BCE"
              />
              <InboxChannelRailButton
                label="Customer Support"
                count={supportConversationCount}
                active={!currentChannel && Boolean(appliedFilters.group) && String(appliedFilters.group) === String(supportWorkstream?.id || "")}
                onClick={() => handleSelectChannelRail("support")}
                icon={<CallOutlinedIcon sx={{ fontSize: 17 }} />}
                accent={C.subtext}
              />
              <InboxChannelRailButton
                label="Main Email"
                count={emailConversationCount}
                active={currentChannel === "email"}
                onClick={() => handleSelectChannelRail("email")}
                icon={<MailOutlineRoundedIcon sx={{ fontSize: 17 }} />}
                accent={C.email}
              />
            </Stack>

            <Stack direction="row" spacing={0.55} alignItems="center" sx={{ flexShrink: 0 }}>
              <IconButton
                size="small"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "12px",
                  border: `1px solid ${alpha(C.text, 0.08)}`,
                  bgcolor: "#FFFFFF",
                  color: C.subtext,
                }}
              >
                <SearchRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "12px",
                  border: `1px solid ${alpha(C.text, 0.08)}`,
                  bgcolor: "#FFFFFF",
                  color: C.subtext,
                }}
              >
                <NotificationsOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: alpha(C.whatsapp, 0.14),
                  color: C.whatsapp,
                  fontWeight: 800,
                  fontSize: 13,
                }}
              >
                {titleLabel.slice(0, 1).toUpperCase()}
              </Avatar>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, p: { xs: 0.8, md: 1.2 } }}>
      {/* ═══════════════════════════════════════════
          3-COLUMN IBX SHELL
          Col 1: Chat list  (220px)
          Col 2: Thread     (1fr)
          Col 3: Detail     (260px)
         ═══════════════════════════════════════════ */}
      <div className={`ibx ibx-shell${activeId ? " has-active" : ""}`}>

        {/* ── COL 1: CHAT LIST ── */}
        <div className="ibx-list-panel">
          {listPane}
        </div>

        {/* ── COL 3: CONVERSATION THREAD ── */}
        <div className="ibx ibx-conv-col">
          {!activeId ? (
            <InboxWelcomeState
              todayNewCount={Number(todayOverview?.new_conversations || 0)}
              todayTouchedCount={Number(todayOverview?.active_conversations || 0)}
              todayAwaitingCount={Number(todayOverview?.awaiting_conversations || 0)}
              todayOverdueCount={Number(todayOverview?.overdue_conversations || 0)}
              onViewUnassigned={handleViewUnassignedConversations}
              onOpenDrafts={handleOpenRecentDrafts}
              onStartConversation={handleStartNewConversation}
              onViewHotLeads={handleOpenHotLeads}
            />
          ) : detailLoading && !activeConversation ? (
            <div className="ibx-thread-skeleton">
              <div className="ibx-thread-sk-header">
                <div className="ibx-skeleton" style={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div className="ibx-skeleton" style={{ height: 13, borderRadius: 6, width: "45%", marginBottom: 6 }} />
                  <div className="ibx-skeleton" style={{ height: 10, borderRadius: 6, width: "30%" }} />
                </div>
              </div>
              <div className="ibx-thread-sk-body">
                {[120, 200, 80, 160, 100, 240, 90].map((w, i) => (
                  <div
                    key={i}
                    className={`ibx-thread-sk-bubble${i % 3 === 2 ? " out" : ""}`}
                  >
                    <div className="ibx-skeleton" style={{ height: 36 + (i % 3) * 14, borderRadius: 12, width: `${w}px`, maxWidth: "72%" }} />
                  </div>
                ))}
              </div>
            </div>
          ) : detailError && !activeConversation ? (
            <div className="ibx-empty">
              <div style={{ color: "#aaaaaa", fontSize: 11 }}>{detailError}</div>
            </div>
          ) : activeConversation ? (
            <>
              {conversationHeader}
              <div
                className="ibx-msg-area scroll-y"
                style={{
                  background: isWhatsAppConversation ? "#EFEAE2" : "#FAFAFA",
                  backgroundImage: isWhatsAppConversation
                    ? "radial-gradient(circle at 24px 24px, rgba(202,189,170,0.18) 1.1px, transparent 1.3px), radial-gradient(circle at 10px 10px, rgba(202,189,170,0.11) 0.9px, transparent 1.1px)"
                    : undefined,
                  backgroundSize: isWhatsAppConversation ? "38px 38px, 26px 26px" : undefined,
                }}
              >
                {(() => {
                  // ── Message windowing ─────────────────────────────────────
                  // Render only the most-recent msgWindowSize messages to keep
                  // the DOM lean. "Load older messages" expands the window by 40
                  // without re-fetching — all messages are already in detailMessages.
                  const totalMessages = detailMessages.length;
                  const windowStart = Math.max(0, totalMessages - msgWindowSize);
                  const visibleMessages = detailMessages.slice(windowStart);
                  const hasOlderMessages = windowStart > 0;

                  return (
                    <>
                      {hasOlderMessages && (
                        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 0.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setMsgWindowSize((n) => n + 40)}
                            sx={{
                              fontSize: 11,
                              borderRadius: "16px",
                              color: "#555",
                              borderColor: "#ddd",
                              px: 2,
                              py: 0.4,
                              "&:hover": { borderColor: "#aaa", bgcolor: "transparent" },
                            }}
                          >
                            Load {Math.min(windowStart, 40)} older messages
                          </Button>
                        </Box>
                      )}
                      {isChatConversation ? (
                        <>
                          <WhatsAppThreadView messages={visibleMessages} />
                          <VoiceCallTimeline voiceCalls={detailVoiceCalls} />
                        </>
                      ) : (
                        <>
                          <EmailThreadView
                            conversation={activeConversation}
                            messages={visibleMessages}
                            onReply={focusReplyComposer}
                            emptyLabel={
                              isVoiceConversation
                                ? "No email-style message events have been captured for this conversation yet."
                                : "No email messages have been captured for this thread yet."
                            }
                          />
                          <VoiceCallTimeline voiceCalls={detailVoiceCalls} />
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              {composerFooter}
            </>
          ) : (
            <div className="ibx-empty">
              <div style={{ color: "#aaaaaa", fontSize: 11 }}>Conversation unavailable</div>
            </div>
          )}
        </div>

        {/* ── COL 4: DETAIL PANEL ── */}
        <div
          className="ibx-detail-col"
          style={{
            display: "flex", flexDirection: "column", fontSize: 12,
            // Override width/opacity when user has collapsed the panel on desktop
            ...(activeConversation && !showConversationDetails ? { width: 0, opacity: 0, pointerEvents: "none" } : {}),
          }}
        >
          {activeConversation ? (
            <>
              {/* Right panel header */}
              <Box sx={{
                px: "12px", py: "0px", height: 58,
                borderBottom: "1px solid #f0f0f0",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                bgcolor: "#FAFBFA",
              }}>
                <Box sx={{
                  width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                  bgcolor: alpha(activeMeta.color, 0.12), color: activeMeta.color,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700,
                }}>
                  {(activeConversation?.customer?.name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase()}
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Box sx={{ fontSize: 12.5, fontWeight: 700, color: "#111111", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {activeConversation?.customer?.name || "Customer"}
                  </Box>
                  <Box sx={{ fontSize: 10.5, color: "#999999", mt: "1px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {activeConversation?.customer?.phone || activeConversation?.customer?.email || activeMeta.label}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: "4px", alignItems: "center", flexShrink: 0 }}>
                  {activeConversation?.inquiry?.status && (
                    <Box sx={{ fontSize: 9.5, fontWeight: 700, px: "6px", py: "2px", borderRadius: "6px", bgcolor: "#dcfce7", color: "#166534" }}>
                      {activeConversation.inquiry.status}
                    </Box>
                  )}
                  {activeConversation?.inquiry?.temperature && (
                    <Box sx={{ fontSize: 9.5, fontWeight: 700, px: "6px", py: "2px", borderRadius: "6px", bgcolor: "#fee2e2", color: "#dc2626" }}>
                      {activeConversation.inquiry.temperature}
                    </Box>
                  )}
                  {/* Collapse button inside panel */}
                  <Tooltip title="Collapse panel">
                    <Box
                      component="button"
                      onClick={() => setShowConversationDetails(false)}
                      sx={{
                        width: 26, height: 26, borderRadius: "7px", border: "none",
                        bgcolor: "transparent", color: "#aaaaaa", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                        "&:hover": { bgcolor: "#eeeeee", color: "#555555" },
                      }}
                    >
                      <ExpandLessRoundedIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Tooltip>
                </Box>
              </Box>

              {/* Scrollable content */}
              <Box sx={{ flexGrow: 1, overflowY: "auto", minHeight: 0 }}>
                <ConversationIntelligenceCard
                  conversation={activeConversation}
                  messages={detailMessages}
                  nextBestAction={activeConversation?.next_best_action || null}
                  onUseSuggestion={handleUseQuoteSuggestion}
                  onOpenAssign={() => openDialog("assign")}
                />
                <CustomerContextPanel
                  channelLabel={activeMeta.label}
                  conversation={activeConversation}
                  onOpenConversation={handleOpenConversationById}
                  onCreateInquiry={handleCreateInquiry}
                  onAssign={() => openDialog("assign")}
                  onOpenInquiry={handleOpenInquiryWorkspace}
                  onResolveConversation={() => handleUpdateStatus("resolved")}
                  onCloseConversation={() => handleUpdateStatus("closed")}
                  onManageFollowUp={() => openDialog("followUp")}
                  onUseSuggestedReply={handleUseQuoteSuggestion}
                />

                {/* ── Feature: Internal Collaboration Thread ── */}
                <TeamNotesPanel
                  conversationId={activeConversation.id}
                  messages={detailMessages}
                  onNoteAdded={() => syncConversationDetail(activeConversation.id, { showLoader: false })}
                />
              </Box>
            </>
          ) : null}
        </div>

      </div>

      {/* ── HIDDEN FILE INPUT ── */}
        </Box>
      </Box>

      <input
        ref={attachmentInputRef}
        type="file"
        multiple
        hidden
        onChange={handleComposerFileSelection}
      />

      {/* ── QUOTE OPPORTUNITIES DRAWER ── */}
      <QuoteOpportunitiesDrawer
        open={quoteQueueOpen}
        onClose={() => setQuoteQueueOpen(false)}
        onSelectConversation={handleSelectQuoteConversation}
        scope={{
          channel: currentChannel || "",
          whatsappLineScope: appliedFilters.whatsappLineScope || "",
          whatsappLineId: appliedFilters.whatsappLineId || "",
          workstreamId: appliedFilters.group || "",
        }}
      />

      {/* ── FILTERS DRAWER ── */}
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 360 },
            background: "linear-gradient(180deg, #FFFFFF 0%, #F4FAF6 100%)",
            borderLeft: `1px solid ${alpha(C.shellMuted, 0.12)}`,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.text }}>
              Filters
            </Typography>
            <IconButton onClick={() => setFiltersOpen(false)} sx={{ width: 34, height: 34, color: C.text }}>
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>

          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {!channelLocked ? (
              <TextField
                select
                size="small"
                label="Channel"
                value={draftFilters.channel}
                onChange={(event) => handleChannelDraftChange(event.target.value)}
              >
                {CHANNEL_RAIL_ITEMS.map((item) => (
                  <MenuItem key={item.label} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}

            {isWhatsAppFilterContext ? (
              <>
                <TextField
                  select
                  size="small"
                  label="WhatsApp view"
                  value={draftFilters.whatsappLineScope}
                  onChange={(event) => {
                    const nextScope = event.target.value;
                    setDraftFilters((prev) => withWhatsAppLineScopeFilter(prev, nextScope));
                  }}
                >
                  <MenuItem value="">All WhatsApp lines</MenuItem>
                  <MenuItem value="broadcast">Broadcast number only</MenuItem>
                  <MenuItem value="rotated_work">Rotated work line only</MenuItem>
                  <MenuItem value="consultant">Direct lines only</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Specific WhatsApp line"
                  value={draftFilters.whatsappLineId}
                  onChange={(event) => {
                    const nextLineId = event.target.value;
                    setDraftFilters((prev) => withWhatsAppLineIdFilter(prev, nextLineId, nextLineId ? "" : prev.whatsappLineScope));
                  }}
                >
                  <MenuItem value="">Any WhatsApp line</MenuItem>
                  {filterableWhatsappLines.map((line) => (
                    <MenuItem key={line.id} value={String(line.id)}>
                      {line.display_name} • {whatsappLineTypeLabel(line.line_type)}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            ) : null}

            <TextField
              select
              size="small"
              label="Assignment"
              value={draftFilters.assigned}
              onChange={(event) => setDraftFilters((prev) => ({ ...prev, assigned: event.target.value }))}
            >
              <MenuItem value="">Any assignment</MenuItem>
              <MenuItem value="mine">Assigned to me</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="unassigned">Unassigned</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Status"
              value={draftFilters.status}
              onChange={(event) => setDraftFilters((prev) => ({ ...prev, status: event.target.value }))}
            >
              <MenuItem value="">Any status</MenuItem>
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Priority"
              value={draftFilters.priority}
              onChange={(event) => setDraftFilters((prev) => ({ ...prev, priority: event.target.value }))}
            >
              <MenuItem value="">Any priority</MenuItem>
              {PRIORITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Workstream"
              value={draftFilters.group}
              onChange={(event) => setDraftFilters((prev) => ({ ...prev, group: event.target.value }))}
            >
              <MenuItem value="">Any workstream</MenuItem>
              {workstreams.map((workstream) => (
                <MenuItem key={workstream.id} value={workstream.id}>
                  {workstream.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Activity"
              value={draftFilters.created}
              onChange={(event) => setDraftFilters((prev) => ({ ...prev, created: event.target.value }))}
            >
              {RELATIVE_WINDOW_OPTIONS.map((option) => (
                <MenuItem key={option.value || "any"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Closed at"
              value={draftFilters.closedAt}
              onChange={(event) => setDraftFilters((prev) => ({ ...prev, closedAt: event.target.value }))}
            >
              {RELATIVE_WINDOW_OPTIONS.map((option) => (
                <MenuItem key={`closed-${option.value || "any"}`} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 2.2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                const resetFilters = makeFilters(initialChannel);
                setDraftFilters(resetFilters);
                setAppliedFilters(resetFilters);
                setFiltersOpen(false);
              }}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Reset
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setAppliedFilters(draftFilters);
                setFiltersOpen(false);
              }}
              sx={{ textTransform: "none", fontWeight: 700, boxShadow: "none" }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* ── HEADER CONTEXT MENU ── */}
      <Menu
        anchorEl={headerMenuAnchor}
        open={Boolean(headerMenuAnchor)}
        onClose={() => setHeaderMenuAnchor(null)}
      >
        {activeConversation?.inquiry?.id ? (
          <MenuItem
            onClick={() => {
              setHeaderMenuAnchor(null);
              handleOpenInquiryWorkspace();
            }}
          >
            Open Inquiry Workspace
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              setHeaderMenuAnchor(null);
              handleCreateInquiry();
            }}
          >
            Create Inquiry
          </MenuItem>
        )}
      </Menu>

      {/* ── TEMPLATE MENU ── */}
      <Menu
        anchorEl={templateMenuAnchor}
        open={Boolean(templateMenuAnchor)}
        onClose={() => setTemplateMenuAnchor(null)}
      >
        {cannedRepliesLoading ? (
          <MenuItem disabled>Loading templates...</MenuItem>
        ) : cannedReplies.length ? (
          cannedReplies.map((reply) => (
            <MenuItem key={reply.id} onClick={() => handleInsertTemplate(reply)}>
              {reply.title || "Untitled template"}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No saved replies available</MenuItem>
        )}
      </Menu>

      {/* ── ACTION DIALOGS ── */}
      <Dialog
        open={newConversationDialogOpen}
        onClose={() => setNewConversationDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Start new conversation</DialogTitle>
        <DialogContent sx={{ pt: "10px !important" }}>
          <Stack spacing={1.4}>
            <TextField
              select
              fullWidth
              label="Channel"
              value={newConversationDraft.channel}
              onChange={(event) => setNewConversationDraft((prev) => ({
                ...createNewConversationDraft({
                  channel: event.target.value,
                  whatsappLineId: event.target.value === "whatsapp"
                    ? (prev.whatsappLineId || "")
                    : prev.whatsappLineId,
                }),
                recipient: prev.recipient,
                contactName: event.target.value === "whatsapp" ? prev.contactName : "",
                contactEmail: event.target.value === "whatsapp" ? prev.contactEmail : "",
                saveContact: event.target.value === "whatsapp" ? prev.saveContact : false,
                subject: event.target.value === "email" ? prev.subject : "",
                message: prev.message,
              }))}
            >
              <MenuItem value="whatsapp">WhatsApp</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="voice">Call</MenuItem>
            </TextField>

            {newConversationDraft.channel === "whatsapp" ? (
              <TextField
                select
                fullWidth
                label="Send from"
                value={newConversationDraft.whatsappLineId}
                onChange={(event) => setNewConversationDraft((prev) => ({ ...prev, whatsappLineId: event.target.value }))}
              >
                <MenuItem value="" disabled>Select a WhatsApp line</MenuItem>
                {launchableWhatsappLines.map((line) => {
                  const connected = Boolean(line?.connection_ready) || String(line?.session_status || "").toLowerCase() === "connected";
                  const lineTypeLabel = whatsappLineTypeLabel(line?.line_type);

                  return (
                    <MenuItem
                      key={line.id}
                      value={String(line.id)}
                      disabled={!connected}
                    >
                      {line.display_name} • {lineTypeLabel}
                      {connected ? "" : " (Cloud API link incomplete)"}
                    </MenuItem>
                  );
                })}
              </TextField>
            ) : null}

            <TextField
              fullWidth
              label={newConversationDraft.channel === "email" ? "Email address" : "Phone number"}
              value={newConversationDraft.recipient}
              onChange={(event) => setNewConversationDraft((prev) => ({ ...prev, recipient: event.target.value }))}
              placeholder={newConversationDraft.channel === "email" ? "customer@example.com" : "+2637..."}
            />

            {newConversationDraft.channel === "whatsapp" ? (
              <>
                <Alert severity="info" sx={{ borderRadius: "12px" }}>
                  Leave the first message blank to save the contact and open a real WhatsApp chat in Unibox without sending anything yet.
                </Alert>
                <TextField
                  fullWidth
                  label="Contact name"
                  value={newConversationDraft.contactName}
                  onChange={(event) => setNewConversationDraft((prev) => ({ ...prev, contactName: event.target.value }))}
                  placeholder="Customer name"
                />
                <TextField
                  fullWidth
                  label="Contact email"
                  value={newConversationDraft.contactEmail}
                  onChange={(event) => setNewConversationDraft((prev) => ({ ...prev, contactEmail: event.target.value }))}
                  placeholder="customer@example.com"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={Boolean(newConversationDraft.saveContact)}
                      onChange={(event) => setNewConversationDraft((prev) => ({ ...prev, saveContact: event.target.checked }))}
                    />
                  )}
                  label="Save this person to contacts before opening the chat"
                />
              </>
            ) : null}

            {newConversationDraft.channel === "email" ? (
              <TextField
                fullWidth
                label="Subject"
                value={newConversationDraft.subject}
                onChange={(event) => setNewConversationDraft((prev) => ({ ...prev, subject: event.target.value }))}
              />
            ) : null}

            <TextField
              fullWidth
              multiline
              minRows={3}
              label={
                newConversationDraft.channel === "voice"
                  ? "Call note"
                  : newConversationDraft.channel === "whatsapp"
                    ? "First message"
                    : "Opening message"
              }
              value={newConversationDraft.message}
              onChange={(event) => setNewConversationDraft((prev) => ({ ...prev, message: event.target.value }))}
              placeholder={
                newConversationDraft.channel === "voice"
                  ? "Optional note for this call"
                  : newConversationDraft.channel === "whatsapp"
                    ? "Optional. Leave blank to open the chat first."
                    : "Opening email message"
              }
              helperText={
                newConversationDraft.channel === "whatsapp"
                  ? "If you type a message now, Unibox will send it and then open the chat."
                  : undefined
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.2 }}>
          <Button onClick={() => setNewConversationDialogOpen(false)} sx={{ textTransform: "none", fontWeight: 700 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLaunchNewConversation}
            disabled={startingConversation}
            sx={{ textTransform: "none", fontWeight: 800, boxShadow: "none" }}
          >
            {newConversationDraft.channel === "whatsapp"
              ? (startingConversation
                ? (String(newConversationDraft.message || "").trim() ? "Sending..." : "Opening chat...")
                : (String(newConversationDraft.message || "").trim()
                  ? "Send & open chat"
                  : (newConversationDraft.saveContact ? "Save contact & open chat" : "Open chat")))
              : `Open ${newConversationDraft.channel === "voice" ? "call" : newConversationDraft.channel}`}
          </Button>
        </DialogActions>
      </Dialog>

      {activeConversation ? (
        <>
          <LinkInquiryDialog
            open={dialogs.linkInquiry}
            onClose={() => closeDialog("linkInquiry")}
            conversation={activeConversation}
            onLinked={(response) => applyConversationPayload(response, "Inquiry linked.")}
          />

          <AssignConversationDialog
            open={dialogs.assign}
            onClose={() => closeDialog("assign")}
            conversation={activeConversation}
            onAssigned={(response) => applyConversationPayload(response, "Handover completed.")}
          />

          <InternalNoteDialog
            open={dialogs.note}
            onClose={() => closeDialog("note")}
            conversation={activeConversation}
            onSaved={(response) => applyConversationPayload(response, "Note saved.")}
          />

          <FollowUpDialog
            open={dialogs.followUp}
            onClose={() => closeDialog("followUp")}
            conversation={activeConversation}
            onSaved={(response) => applyConversationPayload(response, "Follow-up updated.")}
          />

          {!isEmailConversation && !isWhatsAppConversation ? (
            <InitiateVoiceCallDialog
              open={dialogs.liveCall}
              onClose={() => closeDialog("liveCall")}
              conversation={activeConversation}
              onSaved={(response) => applyConversationPayload(response, "Call started.")}
            />
          ) : null}

          <VoiceCallDialog
            open={dialogs.voiceLog}
            onClose={() => closeDialog("voiceLog")}
            conversation={activeConversation}
            onSaved={(response) => applyConversationPayload(response, "Call logged.")}
          />
        </>
      ) : null}

      {/* ── SNACKBAR ── */}
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={3500}
        onClose={() => setSnackbar("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="info" onClose={() => setSnackbar("")} sx={{ width: "100%" }}>
          {snackbar}
        </Alert>
      </Snackbar>
    </>
  );
}
